// ================================
// ADMIN DASHBOARD v2
// ================================
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { QRCodeSVG } from 'qrcode.react';
import ChatThread from '../../components/shared/ChatThread';
import MemberSignupForm from '../../components/shared/MemberSignupForm';

const TABS = ['Dispatch','Menu','Members','Drivers','Staff','Map','Coins','Promos','Messages','Reports','Settings'];

// "Today" stats (Dispatch's Delivered Today/Coins Collected, each driver
// card's "delivered/attempted today") were actually all-time totals —
// nothing ever reset them, they just happened to be small early on. The
// order's own `time` field is time-of-day only, no date, so the only place
// a real date exists per order is the "Delivered at M/D/YYYY..." or
// "Attempted delivery at M/D/YYYY..." stamp completeDelivery()/the
// attempted-delivery route already write into notes. Computing "today"
// live from that — instead of keeping a counter that would need an actual
// reset job — means these numbers are always correct with no cron needed.
//
// That stamp is written in the business's own timezone (America/New_York
// — Railway runs the server in UTC, 4-5 hours ahead), so "today" has to be
// judged against that same timezone here too — comparing plain "M/D/YYYY"
// strings sidesteps the admin's own browser timezone entirely, rather than
// reconstructing a Date and risking a THIRD timezone's interpretation.
function wasClosedToday(order) {
  const match = String(order.notes || '').match(/(?:Delivered at|Attempted delivery at) (\d{1,2}\/\d{1,2}\/\d{4})/);
  if (!match) return false;
  const todayEastern = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  return match[1] === todayEastern;
}

// Leaflet's default marker images don't resolve under webpack — plain divIcons
// sidestep that entirely and let the pins match the app's own palette.
const driverPin = new L.DivIcon({
  className: 'driver-map-pin',
  html: `<div style="background:#1e6bff;color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);">🚗</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15]
});
const destinationPin = new L.DivIcon({
  className: 'destination-map-pin',
  html: `<div style="background:#c0101a;width:24px;height:24px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24]
});

function timeAgo(ts) {
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return `${secs}s ago`;
  return `${Math.round(secs / 60)}m ago`;
}
// Mirrors server/utils/loyalty.js's TIERS/tierForPoints — tier is never
// stored, just the live points balance's highest threshold still cleared.
const POINT_TIERS = [
  { name:'Weedo World', threshold:1000, color:'#c0101a' },
  { name:'Jupiter',     threshold:750,  color:'#f0a020' },
  { name:'Mars',        threshold:500,  color:'#e86a3a' },
  { name:'Mercury',     threshold:250,  color:'#8a94a6' }
];
function tierForPoints(points) {
  return POINT_TIERS.find(t => points >= t.threshold) || null;
}
// Mirrors server/utils/loyalty.js's standing referral payout — just used
// here as the placeholder/label for "what a promo override replaces".
const REFERRAL_REFERRER_POINTS = 100;
const REFERRAL_REFERRER_COINS = 20;

export default function AdminDashboard() {
  const { logout, user, role } = useAuth();
  const socket                = useSocket();
  // An office account only ever sees the sidebar sections its own account
  // was granted (a login-time snapshot — the server independently
  // re-checks the live permission set on every request regardless, see
  // requireSection in middleware/auth.js, so this is purely about what the
  // sidebar shows, never the real access boundary). Admin always sees
  // everything, unchanged.
  const isOffice    = role === 'office';
  const allowedTabs = isOffice ? TABS.filter(t => (user?.permissions || []).includes(t)) : TABS;
  const [tab, setTab]         = useState(() => (isOffice ? (allowedTabs[0] || 'Dispatch') : 'Dispatch'));
  const [showOfficeAccess, setShowOfficeAccess] = useState(false);
  const [orders, setOrders]   = useState([]);
  const [menu, setMenu]       = useState([]);
  const [members, setMembers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [staff, setStaff]     = useState([]);
  const [pending, setPending] = useState([]);
  const [promos, setPromos]   = useState([]);
  const [messageThreads, setMessageThreads] = useState([]);
  const [newOrder, setNewOrder] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (tab !== 'Map') return;
    axios.get('/api/drivers/locations').then(r => setDriverLocations(r.data.drivers || [])).catch(()=>{});
  }, [tab]);

  const [cashoutRequests, setCashoutRequests] = useState([]);
  const [staffCashoutRequests, setStaffCashoutRequests] = useState([]);
  const [coinRequests, setCoinRequests] = useState([]);
  const [cardPrintRequests, setCardPrintRequests] = useState([]); // signups where staff had no physical card — needs printing + delivery
  const [alerts, setAlerts] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]); // [{driverName, lat, lng, updatedAt, destination}]

  useEffect(() => {
    if (!socket) return;
    socket.on('order:new',      (o)  => { setOrders(p => [o, ...p]); setNewOrder(o); setTimeout(() => setNewOrder(null), 4000); });
    socket.on('order:updated',  ({oid, ...u}) => setOrders(p => p.map(o => o.oid === oid ? {...o,...u} : o)));
    socket.on('member:request', (r)  => setPending(p => [r, ...p]));
    socket.on('member:approved',()   => { fetchMembers(); fetchStaff(); });
    socket.on('members:refresh',()   => fetchMembers());
    socket.on('menu:updated',   ()   => fetchMenu());
    socket.on('promos:updated', ()   => fetchPromos());
    socket.on('message:new',    ()   => fetchMessageThreads());
    socket.on('order:issue',    ({oid, issue}) => setAlerts(p => [...p, { type:'issue', oid, issue, time: new Date().toLocaleTimeString() }]));
    socket.on('order:needhelp', ({oid, driverName, time}) => setAlerts(p => [...p, { type:'needhelp', oid, driverName, time }]));
    socket.on('card:needsPrinting', (req) => {
      setAlerts(p => [...p, { type:'cardneeded', mid: req.mid, name: req.name, time: new Date().toLocaleTimeString() }]);
      setCardPrintRequests(p => p.some(x => x.requestId === req.requestId) ? p : [...p, req]);
    });
    // Guarded by requestId since the bootstrap GET (on mount) and this live
    // socket event can both deliver the same request — e.g. a reconnect
    // right as one comes in.
    socket.on('driver:cashout', (req) => setCashoutRequests(p => p.some(x => x.requestId === req.requestId) ? p : [...p, req]));
    socket.on('staff:cashout',  (req) => setStaffCashoutRequests(p => p.some(x => x.requestId === req.requestId) ? p : [...p, req]));
    socket.on('staff:refresh',  ()    => fetchStaff());
    socket.on('drivers:refresh',()    => fetchDrivers());
    socket.on('coin:purchase-request',  (r)  => setCoinRequests(p => [...p, r]));
    socket.on('coin:purchase-resolved', ({requestId}) => setCoinRequests(p => p.filter(r => r.requestId !== requestId)));
    socket.on('driver:location', (loc) => setDriverLocations(p => {
      const others = p.filter(d => d.driverName !== loc.driverName);
      const prevDestination = p.find(d => d.driverName === loc.driverName)?.destination || null;
      return [...others, { ...loc, destination: prevDestination }];
    }));
    socket.on('member:denied', ({phone}) => setPending(p => p.filter(x => x.phone !== phone)));
    return () => { ['order:new','order:updated','member:request','member:approved','members:refresh','menu:updated','promos:updated','message:new','order:issue','order:needhelp','card:needsPrinting','driver:cashout','staff:cashout','staff:refresh','drivers:refresh','coin:purchase-request','coin:purchase-resolved','driver:location','member:denied'].forEach(e => socket.off(e)); };
  }, [socket]);

  const fetchAll     = () => Promise.all([fetchOrders(), fetchMenu(), fetchMembers(), fetchDrivers(), fetchStaff(), fetchPending(), fetchPromos(), fetchMessageThreads(), fetchCashoutRequests(), fetchStaffCashoutRequests(), fetchCardPrintRequests()]);
  const fetchOrders  = async () => { try { const r = await axios.get('/api/orders/all');  setOrders(r.data.orders);  } catch(e){} };
  // /all (not the public /) so admin's own menu manager can see suspended
  // products too, not just what customers currently see.
  const fetchMenu    = async () => { try { const r = await axios.get('/api/menu/all');     setMenu(r.data.menu);      } catch(e){} };
  const fetchPromos  = async () => { try { const r = await axios.get('/api/promos');       setPromos(r.data.promos); } catch(e){} };
  const fetchMessageThreads = async () => { try { const r = await axios.get('/api/messages/threads'); setMessageThreads(r.data.threads); } catch(e){} };
  // Blank, unclaimed cards live in the same sheet rows as real members but
  // aren't members yet — filter them out here so they never show up as a
  // "member" anywhere in the dashboard (stats, roster, dispatch counts).
  // Their inventory count comes from a separate endpoint instead.
  const fetchMembers = async () => { try { const r = await axios.get('/api/members'); setMembers(r.data.members.filter(m => m.status !== 'unclaimed')); } catch(e){} };
  const fetchDrivers = async () => { try { const r = await axios.get('/api/drivers');      setDrivers(r.data.drivers);} catch(e){} };
  const fetchStaff   = async () => { try { const r = await axios.get('/api/staff');         setStaff(r.data.staff);    } catch(e){} };
  const fetchPending = async () => { try { const r = await axios.get('/api/members/pending'); setPending(r.data.pending); } catch(e){} };
  // These two rehydrate the pending-payout lists from the server on every
  // load — previously they only ever came from a live socket event, so a
  // reload (or just not being on this screen when one fired) lost the
  // request with no way to get back to it.
  const fetchCashoutRequests      = async () => { try { const r = await axios.get('/api/drivers/cashout-requests'); setCashoutRequests(r.data.requests); } catch(e){} };
  const fetchStaffCashoutRequests = async () => { try { const r = await axios.get('/api/staff/cashout-requests');   setStaffCashoutRequests(r.data.requests); } catch(e){} };
  const fetchCardPrintRequests    = async () => { try { const r = await axios.get('/api/staff/card-print-requests'); setCardPrintRequests(r.data.requests); } catch(e){} };
  const resolveCardPrintRequest = async (requestId) => {
    try {
      await axios.post(`/api/staff/card-print-requests/${requestId}/resolve`);
      setCardPrintRequests(p => p.filter(r => r.requestId !== requestId));
    } catch(e) { alert(e.response?.data?.error || 'Failed to clear request'); }
  };

  const assignDriver = async (oid, driverName) => {
    if (!driverName) return;
    // An order that already has a driver goes through /reassign instead of
    // /assign — that's the route that tears down and recreates the masked
    // relay session and tells the OLD driver (via order:removed) they no
    // longer have it, neither of which /assign does.
    const existing = orders.find(o => o.oid === oid);
    const isReassign = existing?.assigned && existing.assigned !== driverName;
    // Optimistic update so the dispatch board reflects the change immediately
    setOrders(p => p.map(o => o.oid === oid ? { ...o, assigned: driverName, status: 'Assigned' } : o));
    try {
      if (isReassign) {
        await axios.put(`/api/orders/${oid}/reassign`, { newDriverName: driverName, oldDriverName: existing.assigned });
      } else {
        await axios.put(`/api/orders/${oid}/assign`, { driverName });
      }
      fetchOrders(); // reconcile with server in case anything else changed
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to assign driver — please try again.');
      fetchOrders(); // roll back the optimistic update if it actually failed
    }
  };

  const cancelOrder = async (oid, reason) => {
    try {
      await axios.put(`/api/orders/${oid}/cancel`, { reason: reason || undefined });
      fetchOrders();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to cancel order — please try again.');
    }
  };
  const approveMember = async (req) => {
    try {
      const r = await axios.post('/api/members/approve', req);
      setPending(p => p.filter(x => x.phone !== req.phone));
      alert(`✅ Approved!\nHand them the card for M.I.D. ${r.data.mid}.\nThey'll set their own PIN when they activate it — nothing to text.`);
      fetchMembers();
    } catch(e) { alert(e.response?.data?.error || 'Approval failed'); }
  };
  const denyMember = async (req) => {
    if (!window.confirm(`Deny membership request from ${req.name}?`)) return;
    try {
      await axios.post('/api/members/deny', { phone: req.phone });
      setPending(p => p.filter(x => x.phone !== req.phone));
    } catch(e) { alert('Failed to deny request'); }
  };
  const setMemberStatus = async (mid, status) => {
    try {
      await axios.put(`/api/members/${mid}/status`, { status });
      fetchMembers();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update member status'); }
  };
  const setDriverStatus = async (name, status) => {
    try {
      await axios.put(`/api/drivers/${encodeURIComponent(name)}/status`, { status });
      fetchDrivers();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update driver status'); }
  };
  const setStaffStatus = async (username, status) => {
    try {
      await axios.put(`/api/staff/${encodeURIComponent(username)}/status`, { status });
      fetchStaff();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update staff status'); }
  };
  const topUpWallet = async (mid, amount, dollarCollected) => {
    try {
      const r = await axios.put(`/api/members/${mid}/wallet`, { amount, dollarCollected: dollarCollected || undefined });
      await fetchMembers(); // refresh immediately
      alert(`✅ Added ${amount} coins. New balance: ${r.data.newBalance.toFixed(0)} coins`);
    }
    catch(e) { alert('Failed to update wallet'); }
  };
  const awardPoints = async (mid, amount) => {
    try {
      const r = await axios.post('/api/loyalty/admin/award', { mid, amount });
      await fetchMembers(); // refresh immediately
      alert(`✅ Added ${amount} points. New balance: ${r.data.balance.toFixed(0)} points${r.data.tier ? ` — ${r.data.tier} tier` : ''}`);
    }
    catch(e) { alert(e.response?.data?.error || 'Failed to award points'); }
  };

  // 'Attempted Delivery' and 'Cancelled' are both treated as closed-out
  // everywhere else in the app — the Dispatch board forgetting that was
  // why old no-shows never left the board.
  const active      = orders.filter(o => o.status !== 'Delivered' && o.status !== 'Attempted Delivery' && o.status !== 'Cancelled');
  // "Delivered Today" / "Coins Collected" — genuinely today, not all-time
  // (see wasClosedToday's comment for why that was wrong before).
  const deliveredToday = orders.filter(o => o.status === 'Delivered' && wasClosedToday(o));
  const delivered   = deliveredToday.length;
  const totalCoins  = deliveredToday.reduce((s,o) => s + parseFloat(String(o.total).replace(/[^0-9.]/g,''))||0, 0);
  const tabIcons    = { Dispatch:'📌', Menu:'🌿', Members:'👤', Drivers:'🚗', Staff:'📣', Map:'🗺️', Coins:'🪙', Promos:'📢', Messages:'💬', Reports:'📊', Settings:'⚙️' };
  const unreadMessages = messageThreads.reduce((s,t) => s + t.unreadCount, 0);

  const sidebarStyle = (t) => ({
    display:'flex', alignItems:'center', gap:10, padding:'10px 18px',
    fontSize:13, cursor:'pointer',
    color: tab===t ? '#e8e8e8' : '#3a5a8a',
    borderLeft: tab===t ? '2px solid #1e6bff' : '2px solid transparent',
    background: tab===t ? '#0a0f20' : 'transparent', transition:'all 0.2s'
  });

  return (
    <div className="admin-shell">
      {/* Sidebar */}
      <div style={{ width:180, background:'#06091a', borderRight:'1px solid #0a1628', display:'flex', flexDirection:'column', padding:'20px 0', flexShrink:0 }}>
        <div style={{ padding:'0 18px 20px', borderBottom:'1px solid #0a1628', marginBottom:16 }}>
          <div style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:22, color:'#c0101a', letterSpacing:2 }}>W WORLD</div>
          <div style={{ fontSize:10, color:'#1e3a6a', letterSpacing:2, textTransform:'uppercase' }}>Admin Panel</div>
        </div>
        {allowedTabs.map(t => (
          <div key={t} onClick={() => setTab(t)} style={sidebarStyle(t)}>
            <span style={{ fontSize:14 }}>{tabIcons[t]}</span>{t}
            {t==='Dispatch' && (active.length + cardPrintRequests.length) > 0 && <span style={{ marginLeft:'auto', background:'#c0101a', color:'#fff', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{active.length + cardPrintRequests.length}</span>}
            {t==='Members' && pending.length > 0 && <span style={{ marginLeft:'auto', background:'#f0a020', color:'#0a0a0a', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{pending.length}</span>}
            {t==='Coins' && coinRequests.length > 0 && <span style={{ marginLeft:'auto', background:'#f0a020', color:'#0a0a0a', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{coinRequests.length}</span>}
            {t==='Drivers' && cashoutRequests.length > 0 && <span style={{ marginLeft:'auto', background:'#f0a020', color:'#0a0a0a', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{cashoutRequests.length}</span>}
            {t==='Staff' && staffCashoutRequests.length > 0 && <span style={{ marginLeft:'auto', background:'#f0a020', color:'#0a0a0a', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{staffCashoutRequests.length}</span>}
            {t==='Messages' && unreadMessages > 0 && <span style={{ marginLeft:'auto', background:'#c0101a', color:'#fff', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>{unreadMessages}</span>}
          </div>
        ))}
        <div style={{ marginTop:'auto', padding:'16px 18px', borderTop:'1px solid #0a1628' }}>
          <div style={{ fontSize:11, color:'#3a5a8a' }}>System Status</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#1e6bff', marginTop:4 }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#1e6bff' }} />Live
          </div>
          {/* Office accounts never see this — creating one is admin-only,
              on purpose, so an office account can't hand itself (or anyone
              else) more access than it was actually given. */}
          {role === 'admin' && (
            <button onClick={() => setShowOfficeAccess(true)} style={{ marginTop:12, display:'block', background:'none', border:'none', color:'#1e6bff', fontSize:12, cursor:'pointer', padding:0 }}>
              + Office Access
            </button>
          )}
          <button onClick={logout} style={{ marginTop:12, background:'none', border:'none', color:'#3a5a8a', fontSize:12, cursor:'pointer', padding:0 }}>Sign Out</button>
        </div>
      </div>

      {showOfficeAccess && <OfficeAccessPanel onClose={() => setShowOfficeAccess(false)} />}

      {/* Main */}
      <div style={{ flex:1, overflowY:'auto', padding:24, position:'relative' }}>
        {newOrder && (
          <div style={{ position:'fixed', top:16, right:16, background:'#06091a', border:'1px solid #1e6bff', borderRadius:14, padding:'14px 20px', zIndex:100 }}>
            <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:2 }}>🔔 New Order</p>
            <p style={{ fontSize:14, color:'#e8e8e8' }}>{newOrder.name} — {newOrder.total}</p>
          </div>
        )}

        {/* Need Help / Issue Alerts */}
        {alerts.length > 0 && (
          <div style={{ position:'fixed', top:16, left:200, right:16, zIndex:99, display:'flex', flexDirection:'column', gap:8, maxWidth:400 }}>
            {alerts.slice(-3).map((a, i) => (
              <div key={i} style={{ background:'#1a0800', border:'1px solid #f0a020', borderRadius:12, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <p style={{ fontSize:12, color:'#f0a020', marginBottom:2 }}>
                    {a.type === 'needhelp' ? '🆘 Driver Needs Help' : a.type === 'cardneeded' ? '🪪 Card Needs Printing' : '⚠️ Delivery Issue'}
                  </p>
                  <p style={{ fontSize:13, color:'#e8e8e8' }}>
                    {a.type === 'needhelp' ? `${a.driverName} on order ${a.oid}` : a.type === 'cardneeded' ? `${a.name} — M.I.D. ${a.mid}, no card in hand` : `Order ${a.oid}: ${a.issue}`}
                  </p>
                </div>
                <button onClick={() => setAlerts(p => p.filter((_, j) => j !== i))}
                  style={{ background:'none', border:'none', color:'#f0a020', cursor:'pointer', fontSize:16 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'Dispatch' && <DispatchTab orders={orders} drivers={drivers} active={active} delivered={delivered} totalCoins={totalCoins} members={members} staff={staff} onAssign={assignDriver} onCancel={cancelOrder} onRefresh={fetchOrders} cardPrintRequests={cardPrintRequests} onResolveCardPrint={resolveCardPrintRequest} />}
        {tab === 'Menu'     && <MenuTab menu={menu} onRefresh={fetchMenu} />}
        {tab === 'Members'  && <MembersTab members={members} orders={orders} staff={staff} drivers={drivers} pending={pending} onApprove={approveMember} onDeny={denyMember} onTopUp={topUpWallet} onAwardPoints={awardPoints} onSetStatus={setMemberStatus} onRefresh={fetchMembers} />}
        {tab === 'Drivers'  && <DriversTab drivers={drivers} orders={orders} members={members} staff={staff} cashoutRequests={cashoutRequests} setCashoutRequests={setCashoutRequests} onSetStatus={setDriverStatus} onRefresh={fetchDrivers} />}
        {tab === 'Staff'    && <StaffTab staff={staff} members={members} orders={orders} drivers={drivers} cashoutRequests={staffCashoutRequests} setCashoutRequests={setStaffCashoutRequests} onSetStatus={setStaffStatus} onRefresh={fetchStaff} />}
        {tab === 'Map'      && <LiveMapTab driverLocations={driverLocations} />}
        {tab === 'Coins'    && <CoinsTab coinRequests={coinRequests} setCoinRequests={setCoinRequests} />}
        {tab === 'Promos'   && <PromosTab promos={promos} onRefresh={fetchPromos} />}
        {tab === 'Messages' && <MessagesTab threads={messageThreads} onRefresh={fetchMessageThreads} />}
        {tab === 'Reports'  && <ReportsTab orders={orders} members={members} drivers={drivers} staff={staff} totalCoins={totalCoins} onTopUp={topUpWallet} />}
        {tab === 'Settings' && <SettingsTab />}
      </div>
    </div>
  );
}

// ================================
// DISPATCH TAB
// ================================
function DispatchTab({ orders, drivers, active, delivered, totalCoins, members, staff, onAssign, onCancel, onRefresh, cardPrintRequests, onResolveCardPrint }) {
  const [search, setSearch] = useState('');
  const [detailOrder, setDetailOrder] = useState(null); // order row whose transaction detail is open
  const [driverProfile, setDriverProfile] = useState(null); // driver whose profile/work history is open
  const [memberProfile, setMemberProfile] = useState(null); // member whose profile/order history is open
  const [staffProfile, setStaffProfile] = useState(null); // staffer opened from a member's "signed up by" link
  const statusClass = (s) => {
    if (!s || s==='Pending') return 'pill-pending';
    if (s==='Assigned') return 'pill-assigned';
    if (s==='Driver Arrived') return 'pill-out';
    if (s==='Delivered') return 'pill-delivered';
    if (s==='Cancelled') return 'pill-cancelled';
    if (s==='Issue') return 'pill-pending';
    return 'pill-pending';
  };

  // Default view is just today's open work (active) so the board actually
  // clears out as orders close — but a completed order can still have an
  // issue after the fact, so typing a search reaches across the FULL
  // history (every status, not just active) to find it again.
  const q = search.trim().toLowerCase();
  const visibleOrders = q
    ? orders.filter(o => [o.oid, o.name, o.mid, o.phone, o.summary].some(f => String(f||'').toLowerCase().includes(q)))
    : active;

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Dispatch Board</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Live order queue — real-time updates</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          { label:'Active Orders',   val:active.length,             color:'#1e6bff' },
          { label:'Delivered Today', val:delivered,                  color:'#1a9e5a' },
          { label:'Coins Collected Today', val:`${totalCoins.toFixed(0)}`, color:'#e8e8e8' },
          { label:'Total Members',   val:members.length,             color:'#e8e8e8' },
        ].map(m => (
          <div key={m.label} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
            <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{m.label}</p>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:28, color:m.color, letterSpacing:1 }}>{m.val}</p>
          </div>
        ))}
      </div>

      {cardPrintRequests.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, color:'#f0a020', marginBottom:12 }}>🪪 Cards To Print & Deliver</p>
          {cardPrintRequests.map(r => (
            <div key={r.requestId} style={{ background:'#1a1200', border:'1px solid #4a3000', borderRadius:12, padding:'12px 16px', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
              <div>
                <p style={{ fontSize:14, color:'#e8e8e8' }}>{r.name} — M.I.D. <strong>{r.mid}</strong></p>
                <p style={{ fontSize:11, color:'#3a5a8a', marginTop:2 }}>{r.phone} · signed up by {r.staffName} · {r.requestedAt}</p>
              </div>
              <button onClick={() => onResolveCardPrint(r.requestId)} style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:9, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                ✓ Printed & Delivered
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:12, flexWrap:'wrap' }}>
        <h3 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1 }}>{q ? 'Search Results' : 'Active Orders'}</h3>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search any order — OID, name, MID, phone..."
            style={{ background:'#0a0f20', border:'1px solid #0d1e3a', color:'#e8e8e8', borderRadius:8, padding:'6px 12px', fontSize:12, fontFamily:'DM Sans,sans-serif', width:260 }} />
          <button className="btn-blue" onClick={onRefresh} style={{ fontSize:12, padding:'6px 14px' }}>Refresh</button>
        </div>
      </div>
      {q && <p style={{ fontSize:11, color:'#3a5a8a', marginTop:-6, marginBottom:12 }}>Searching every order, including already-delivered ones — clear the search to go back to just today's active queue.</p>}
      {!q && visibleOrders.length === 0 && (
        <p style={{ fontSize:12, color:'#3a5a8a', padding:'16px 0' }}>No active orders — the board's clear. Delivered and no-show orders are still searchable above.</p>
      )}
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr>
          {['OID','Member','Summary','Total','Driver','Status'].map(h => (
            <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'8px 12px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {visibleOrders.map(o => (
            <tr key={o.oid} onMouseEnter={e=>e.currentTarget.style.background='#06091a'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <td style={{ padding:'10px 12px' }}>
                <button onClick={()=>setDetailOrder(o)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'Bebas Neue,sans-serif', color:'#1e6bff', letterSpacing:1, fontSize:13, textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.oid}</button>
              </td>
              <td style={{ padding:'10px 12px' }}>
                {(() => {
                  const m = members.find(mm => String(mm.mid) === String(o.mid));
                  return m
                    ? <button onClick={()=>setMemberProfile(m)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.name}</button>
                    : o.name;
                })()}
              </td>
              <td style={{ padding:'10px 12px', color:'#888', maxWidth:200 }}>{o.summary}</td>
              <td style={{ padding:'10px 12px', fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a' }}>{o.total}</td>
              <td style={{ padding:'10px 12px' }}>
                {o.status === 'Delivered' || o.status === 'Cancelled'
                  ? (() => {
                      const d = drivers.find(dr => dr.name === o.assigned);
                      return d
                        ? <button onClick={()=>setDriverProfile(d)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#3a5a8a', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.assigned}</button>
                        : <span style={{ color:'#3a5a8a' }}>{o.assigned || '—'}</span>;
                    })()
                  : <select key={o.assigned || 'unassigned'} defaultValue={o.assigned||''} onChange={e=>onAssign(o.oid,e.target.value)}
                      style={{ background:'#0a0f20', border:'1px solid #0d1e3a', color:'#e8e8e8', borderRadius:7, padding:'4px 8px', fontSize:12, fontFamily:'DM Sans,sans-serif' }}>
                      <option value=''>Assign...</option>
                      {drivers.filter(d => d.status !== 'suspended').map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                }
              </td>
              <td style={{ padding:'10px 12px' }}>
                <span className={`pill ${statusClass(o.status)}`}>{o.status||'Pending'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {detailOrder && <OrderDetailModal order={detailOrder} drivers={drivers} members={members} staff={staff} orders={orders} onAssign={onAssign} onCancel={onCancel} onRefresh={onRefresh} onClose={()=>setDetailOrder(null)} />}
      {driverProfile && <DriverProfileModal driver={driverProfile} orders={orders} members={members} staff={staff} onClose={()=>setDriverProfile(null)} />}
      {memberProfile && (
        <MemberProfileModal
          member={memberProfile}
          orders={orders}
          members={members}
          staff={staff}
          onSelectMember={setMemberProfile}
          onSelectStaff={(username) => setStaffProfile(staff.find(s => s.username === username))}
          onClose={() => setMemberProfile(null)}
        />
      )}
      {staffProfile && <StaffProfileModal staffMember={staffProfile} members={members} orders={orders} drivers={drivers} staff={staff} onClose={()=>setStaffProfile(null)} />}
    </div>
  );
}

// ================================
// ORDER TRANSACTION DETAIL — driver payout, points, referral payout, and
// the customer's before/after balance for one specific delivery. Fetched
// on open rather than carried in the orders list, since it's only ever
// needed for the one order someone actually clicks into.
// ================================
function OrderDetailModal({ order, drivers, members, staff, orders, onAssign, onCancel, onRefresh, onClose }) {
  const [txn, setTxn] = useState(undefined); // undefined = loading, null = none recorded
  const [driverProfile, setDriverProfile] = useState(null); // driver whose own profile is open from here
  const [memberProfile, setMemberProfile] = useState(null); // customer whose own profile is open from here
  const [staffProfile, setStaffProfile] = useState(null); // staffer opened from the customer's "signed up by" link
  // Reassign/cancel only make sense for an order that's still actually
  // moving — once it's closed out (delivered, no-show, or already
  // cancelled) there's nothing left for dispatch to redirect.
  const isClosed = ['Delivered', 'Attempted Delivery', 'Cancelled'].includes(order.status);
  const [reassigning, setReassigning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showDriverChat, setShowDriverChat] = useState(false);
  const [showCustomerChat, setShowCustomerChat] = useState(false);

  useEffect(() => {
    axios.get(`/api/orders/${encodeURIComponent(order.oid)}/transaction`)
      .then(r => setTxn(r.data.transaction))
      .catch(() => setTxn(null));
  }, [order.oid]);

  async function handleReassign(driverName) {
    if (!driverName || driverName === order.assigned || !onAssign) return;
    setReassigning(true);
    try { await onAssign(order.oid, driverName); }
    finally { setReassigning(false); }
  }

  async function handleCancel() {
    if (!onCancel) return;
    setCancelling(true);
    try {
      await onCancel(order.oid, cancelReason.trim());
      setShowCancelConfirm(false);
      onClose();
    } catch (e) {}
    finally { setCancelling(false); }
  }

  const row = (label, value) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #0a1628', fontSize:12 }}>
      <span style={{ color:'#3a5a8a' }}>{label}</span>
      <span style={{ color:'#e8e8e8' }}>{value}</span>
    </div>
  );

  // A name is turned into a clickable link to that driver's own profile only
  // when `drivers` was actually passed in and a match is found — some call
  // sites (e.g. a driver's own profile, viewing their own orders) skip this
  // since linking to yourself from your own history isn't useful.
  const driverName = (name) => {
    const d = drivers?.find(dr => dr.name === name);
    return d
      ? <button onClick={()=>setDriverProfile(d)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#1e6bff', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{name}</button>
      : (name || '—');
  };
  const member = members?.find(m => String(m.mid) === String(order.mid));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:460, maxHeight:'85vh', overflowY:'auto', background:'#06091a', border:'1px solid #1e3a6a', borderRadius:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #0a1628' }}>
          <div>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, color:'#1e6bff' }}>{order.oid}</p>
            <p style={{ fontSize:11, color:'#3a5a8a' }}>
              {member
                ? <button onClick={()=>setMemberProfile(member)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#3a5a8a', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{order.name}</button>
                : order.name}
              {' '}• {order.status || 'Pending'}
            </p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ padding:'16px 20px' }}>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Order</p>
          {row('Summary', order.summary || '—')}
          {row('Total', order.total || '—')}
          {row('Driver assigned', driverName(order.assigned))}
          {row('Placed', order.time || '—')}

          {/* Quick contact — straight from the order, no detour through
              Members/Drivers to find the person first. */}
          <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
            {order.assigned && (
              <button onClick={()=>setShowDriverChat(true)} style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:9, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                💬 Message Driver
              </button>
            )}
            {member && (
              <button onClick={()=>setShowCustomerChat(true)} style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:9, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                💬 Message Customer
              </button>
            )}
          </div>

          {/* Reassign / cancel — for handling a bad delivery or a driver
              problem without having to leave this order's own detail view. */}
          {!isClosed && (onAssign || onCancel) && (
            <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #0a1628' }}>
              {onAssign && (
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 }}>
                    {order.assigned ? 'Reassign To' : 'Assign Driver'}
                  </label>
                  <select defaultValue="" disabled={reassigning} onChange={e => { handleReassign(e.target.value); e.target.value = ''; }}
                    style={{ width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:12, fontFamily:'DM Sans,sans-serif' }}>
                    <option value="">{reassigning ? 'Reassigning…' : 'Choose a driver...'}</option>
                    {drivers?.filter(d => d.status !== 'suspended' && d.name !== order.assigned).map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
              )}
              {onCancel && (
                showCancelConfirm ? (
                  <div style={{ background:'#1a0505', border:'1px solid #3a0a0a', borderRadius:10, padding:12 }}>
                    <p style={{ fontSize:12, color:'#e8e8e8', marginBottom:8 }}>Cancel this order? The customer and driver (if assigned) are both notified.</p>
                    <input value={cancelReason} onChange={e=>setCancelReason(e.target.value)} placeholder="Reason (optional, shown to the customer)"
                      style={{ width:'100%', padding:'8px 10px', marginBottom:10, background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:12, fontFamily:'DM Sans,sans-serif' }} />
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={handleCancel} disabled={cancelling} className="btn-danger" style={{ fontSize:12 }}>
                        {cancelling ? 'Cancelling…' : 'Yes, Cancel Order'}
                      </button>
                      <button onClick={()=>setShowCancelConfirm(false)} disabled={cancelling} style={{ background:'none', border:'1px solid #0d1e3a', color:'#3a5a8a', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        Never Mind
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={()=>setShowCancelConfirm(true)} className="btn-danger" style={{ fontSize:12 }}>
                    Cancel Order
                  </button>
                )
              )}
            </div>
          )}

          {txn === undefined && <p style={{ fontSize:12, color:'#3a5a8a', marginTop:16 }}>Loading transaction details…</p>}

          {txn === null && (
            <p style={{ fontSize:12, color:'#3a5a8a', marginTop:16, lineHeight:1.6 }}>
              No detailed transaction record for this order — either it hasn't been delivered yet, or it was delivered before this tracking existed. Only the basic order info above was ever recorded for it.
            </p>
          )}

          {txn && (
            <>
              <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, margin:'18px 0 6px' }}>Customer Balance</p>
              {row('Before', `${txn.customer?.balanceBefore ?? '—'} coins`)}
              {row('Coins deducted', `${txn.customer?.coinsDeducted ?? '—'} coins`)}
              {row('After', `${txn.customer?.balanceAfter ?? '—'} coins`)}

              <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, margin:'18px 0 6px' }}>Driver Payout</p>
              {txn.driver ? (
                <>
                  {row('Driver', driverName(txn.driver.name))}
                  {row('Pay type', txn.driver.payType === 'commission' ? `Commission (${txn.driver.rate}%)` : `Flat (${txn.driver.rate} coins)`)}
                  {row('Earned this delivery', `${txn.driver.earned} coins`)}
                </>
              ) : (
                <p style={{ fontSize:12, color:'#3a5a8a' }}>No driver was on record for this delivery.</p>
              )}

              <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, margin:'18px 0 6px' }}>Loyalty Points</p>
              {txn.points ? (
                <>
                  {row('Earned', `+${txn.points.earned} points`)}
                  {row('Balance after', `${txn.points.balanceAfter} points`)}
                  {txn.points.tierUp && row('Tier reached', txn.points.tierAfter)}
                </>
              ) : (
                <p style={{ fontSize:12, color:'#3a5a8a' }}>No points earned on this order.</p>
              )}

              {txn.referral && (
                <>
                  <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, margin:'18px 0 6px' }}>Referral Bonus (first order)</p>
                  {row('Referrer M.I.D.', txn.referral.referrerMid)}
                  {row('Referrer earned', `+${txn.referral.referrerPointsAwarded} points, +${txn.referral.referrerCoinsAwarded} coins`)}
                  {row('Customer welcome bonus', `+${txn.referral.friendWelcomeBonusCoins} coins`)}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {driverProfile && <DriverProfileModal driver={driverProfile} orders={orders || []} members={members} staff={staff} onClose={()=>setDriverProfile(null)} />}
      {memberProfile && (
        <MemberProfileModal
          member={memberProfile}
          orders={orders || []}
          members={members}
          staff={staff}
          onSelectMember={setMemberProfile}
          onSelectStaff={(username) => setStaffProfile(staff?.find(s => s.username === username))}
          onClose={() => setMemberProfile(null)}
        />
      )}
      {staffProfile && <StaffProfileModal staffMember={staffProfile} members={members} orders={orders || []} drivers={drivers} staff={staff} onClose={()=>setStaffProfile(null)} />}

      {showDriverChat && order.assigned && (
        <ChatThread
          title={`Message ${order.assigned}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/driver/${encodeURIComponent(order.assigned)}`}
          postUrl={`/api/messages/thread/driver/${encodeURIComponent(order.assigned)}`}
          onClose={()=>setShowDriverChat(false)}
        />
      )}
      {showCustomerChat && member && (
        <ChatThread
          title={`Message ${order.name}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/customer/${member.mid}`}
          postUrl={`/api/messages/thread/customer/${member.mid}`}
          onClose={()=>setShowCustomerChat(false)}
        />
      )}
    </div>
  );
}

// ================================
// MENU TAB
// ================================
const PRODUCT_TYPES = ['Flower','Edibles','Tinctures','Carts','Concentrates','Other'];
const PRODUCT_TYPE_ICONS = { Flower:'🌿', Edibles:'🍬', Tinctures:'💧', Carts:'🔋', Concentrates:'🍯', Other:'📦' };
// Just a head start per type when a blank price row is added — admin can
// still type anything (e.g. "50mg", "6-pack", "1.5ml") since a size is
// really just a free-text label the way the backend already treats it.
const SIZE_HINTS = { Flower:'e.g. 3.5g', Edibles:'e.g. 10-pack', Tinctures:'e.g. 30ml', Carts:'e.g. 1g cart', Concentrates:'e.g. 1g', Other:'e.g. unit' };

function MenuTab({ menu, onRefresh }) {
  const socket = useSocket();
  const imgRefs = useRef([null, null, null, null]);
  const blankForm = { strain:'', category:'', productType:'Flower', images:['','','',''], notes:'', quantity:'' };
  const [form, setForm]     = useState(blankForm);
  const [pricingStyle, setPricingStyle] = useState('options'); // 'options' (discrete list) or 'gram' (bulk slider)
  const [priceRows, setPriceRows] = useState([{ size:'', coins:'', points:'' }]);
  const [gramCoins, setGramCoins] = useState('');
  const [gramPoints, setGramPoints] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null); // which of the 4 photo slots is mid-upload
  const [editingStrain, setEditingStrain] = useState(null); // the product's name BEFORE this edit, or null when adding new
  const [togglingStrain, setTogglingStrain] = useState(null); // mid-suspend/reactivate
  const [showSuspended, setShowSuspended] = useState(false);

  // Categories — the picklist products are categorized under. Self-contained
  // to this tab (nothing else in the dashboard needs it).
  const [categories, setCategories] = useState([]);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => {
    if (!socket) return;
    socket.on('categories:updated', fetchCategories);
    return () => socket.off('categories:updated', fetchCategories);
  }, [socket]);

  async function fetchCategories() {
    try { const r = await axios.get('/api/categories'); setCategories(r.data.categories); } catch(e) {}
  }

  async function addCategory(e) {
    e.preventDefault();
    if (!newCatName.trim()) return;
    try {
      await axios.post('/api/categories', { name: newCatName.trim() });
      setNewCatName('');
      fetchCategories();
    } catch(e) { alert(e.response?.data?.error || 'Failed to add category'); }
  }

  async function saveCategoryRename(id) {
    if (!editingCatName.trim()) { setEditingCatId(null); return; }
    try {
      await axios.put(`/api/categories/${id}`, { name: editingCatName.trim() });
      setEditingCatId(null);
      fetchCategories();
      onRefresh(); // products carrying the old name need to show the new one
    } catch(e) { alert(e.response?.data?.error || 'Failed to rename category'); }
  }

  async function deleteCategory(id, name) {
    if (!window.confirm(`Remove "${name}" from the category list? Products already using it keep their category — this only affects what's offered for new/edited products.`)) return;
    try {
      await axios.delete(`/api/categories/${id}`);
      fetchCategories();
    } catch(e) { alert('Failed to delete category'); }
  }

  // A blank, untouched Add form defaults to the first category once the
  // list loads — editing an existing product always keeps its own value.
  useEffect(() => {
    if (categories.length && !form.category && !editingStrain) {
      setForm(p => ({ ...p, category: categories[0].name }));
    }
  }, [categories]);

  const upd = (f,v) => setForm(p=>({...p,[f]:v}));
  const updRow = (i, field, val) => setPriceRows(rows => rows.map((r,idx) => idx===i ? {...r,[field]:val} : r));
  const addRow = () => setPriceRows(rows => [...rows, { size:'', coins:'', points:'' }]);
  const removeRow = (i) => setPriceRows(rows => rows.filter((_,idx) => idx!==i));

  // Used to just stash a browser-local blob: URL via URL.createObjectURL —
  // looked right in the preview (same tab, same session) but was never an
  // actual file anywhere, so it saved a dead link: broken for every other
  // device, and broken even on a reload of this same page. Upload for real,
  // same pattern as the promo image upload above. Up to 4 independent slots.
  const handleImg = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await axios.post('/api/menu/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, images: p.images.map((u,i) => i===idx ? r.data.url : u) }));
    } catch (err) { alert(err.response?.data?.error || 'Image upload failed'); }
    finally { setUploadingIdx(null); }
    e.target.value = ''; // so picking the same file again (after removing it) still fires onChange
  };

  const removeImg = (idx) => setForm(p => ({ ...p, images: p.images.map((u,i) => i===idx ? '' : u) }));

  const resetForm = () => {
    setForm(blankForm);
    setPriceRows([{ size:'', coins:'', points:'' }]);
    setGramCoins('');
    setGramPoints('');
    setPricingStyle('options');
    setEditingStrain(null);
  };

  // Loads an existing product into the form instead of starting blank, so
  // changing it means editing in place rather than re-entering everything
  // and ending up with a duplicate.
  const editProduct = (item) => {
    setEditingStrain(item.strain);
    setForm({
      strain: item.strain,
      category: item.category,
      productType: item.productType || 'Flower',
      images: [item.imageUrl || '', item.imageUrl2 || '', item.imageUrl3 || '', item.imageUrl4 || ''],
      notes: item.notes || '',
      quantity: item.quantity === null || item.quantity === undefined ? '' : String(item.quantity)
    });
    if (item.type === 'gram') {
      setPricingStyle('gram');
      const p = item.prices && item.prices[0];
      setGramCoins(p ? String(p.coins) : '');
      setGramPoints(p && p.points ? String(p.points) : '');
      setPriceRows([{ size:'', coins:'', points:'' }]);
    } else {
      setPricingStyle('options');
      setPriceRows(item.prices && item.prices.length > 0
        ? item.prices.map(p => ({ size: p.size, coins: String(p.coins), points: p.points ? String(p.points) : '' }))
        : [{ size:'', coins:'', points:'' }]);
      setGramCoins('');
      setGramPoints('');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Suspend/reactivate — hides it from the customer menu without touching
  // any of its data, unlike the old delete-and-lose-everything behavior.
  const toggleActive = async (item) => {
    setTogglingStrain(item.strain);
    try {
      await axios.put(`/api/menu/${encodeURIComponent(item.strain)}/active`, { active: !item.active });
      onRefresh();
    } catch (e) { alert(`Failed to ${item.active ? 'suspend' : 'reactivate'} product`); }
    finally { setTogglingStrain(null); }
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const prices = pricingStyle === 'gram'
        ? (gramCoins ? [{ size:'per_gram', coins:parseFloat(gramCoins), points: gramPoints ? parseFloat(gramPoints) : 0 }] : [])
        : priceRows.filter(r => r.size && r.coins).map(r => ({ size:r.size, coins:parseFloat(r.coins), points: r.points ? parseFloat(r.points) : 0 }));

      const payload = {
        strain: form.strain, category: form.category, productType: form.productType, notes: form.notes,
        type: pricingStyle === 'gram' ? 'gram' : 'weight', prices,
        imageUrl: form.images[0] || '', imageUrl2: form.images[1] || '', imageUrl3: form.images[2] || '', imageUrl4: form.images[3] || '',
        quantity: form.quantity === '' ? null : parseFloat(form.quantity)
      };

      if (editingStrain) {
        await axios.put(`/api/menu/${encodeURIComponent(editingStrain)}`, payload);
      } else {
        await axios.post('/api/menu', payload);
      }
      resetForm();
      onRefresh();
    } catch(e) { alert(editingStrain ? 'Failed to save changes' : 'Failed to add product'); }
    finally { setSaving(false); }
  };

  const catIcons = { Cheapies:'🌿', Mids:'🍃', ZaZa:'⭐', Gas:'🔥' };
  const fldStyle = { width:'100%', padding:'10px 12px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:9, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' };
  const lblStyle = { fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 };

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Menu Manager</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Add or remove products — flower, edibles, tinctures, carts, whatever you carry</p>

      {/* Categories */}
      <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: showCatManager ? 12 : 0 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:16, letterSpacing:1 }}>Categories</p>
          <button onClick={()=>setShowCatManager(!showCatManager)} style={{ background:'transparent', color:'#1e6bff', border:'1px solid #1e3a6a', padding:'6px 14px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            {showCatManager ? 'Done' : 'Manage'}
          </button>
        </div>
        {showCatManager && (
          <>
            <form onSubmit={addCategory} style={{ display:'flex', gap:8, marginBottom:12 }}>
              <input value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="New category name"
                style={{ ...fldStyle, flex:1 }} />
              <button type="submit" style={{ background:'#1e3a8a', color:'#fff', border:'none', padding:'0 18px', borderRadius:9, cursor:'pointer', fontSize:12, fontFamily:'DM Sans,sans-serif' }}>Add</button>
            </form>
            {categories.map(c => (
              <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid #0a1628' }}>
                {editingCatId === c.id ? (
                  <input value={editingCatName} onChange={e=>setEditingCatName(e.target.value)} autoFocus
                    onKeyDown={e=>{ if (e.key==='Enter') saveCategoryRename(c.id); if (e.key==='Escape') setEditingCatId(null); }}
                    onBlur={()=>saveCategoryRename(c.id)} style={{ ...fldStyle, padding:'5px 8px', fontSize:12, flex:1, marginRight:10 }} />
                ) : (
                  <span style={{ fontSize:13 }}>{c.name}</span>
                )}
                <div style={{ display:'flex', gap:10, flexShrink:0 }}>
                  <button onClick={()=>{ setEditingCatId(c.id); setEditingCatName(c.name); }} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:11, cursor:'pointer', textDecoration:'underline' }}>Rename</button>
                  <button onClick={()=>deleteCategory(c.id, c.name)} style={{ background:'none', border:'none', color:'#c0101a', fontSize:11, cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Add/Edit Product Form */}
      <div style={{ background:'#06091a', border: editingStrain ? '1px solid #1e3a6a' : '1px solid #0a1628', borderRadius:16, padding:20, marginBottom:24 }}>
        <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:16 }}>
          {editingStrain ? `Edit Product — ${editingStrain}` : 'Add New Product'}
        </p>
        <form onSubmit={submitProduct}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
            <div><label style={lblStyle}>Product Name</label><input style={fldStyle} type="text" placeholder="e.g. Gelato #41" value={form.strain} onChange={e=>upd('strain',e.target.value)} required /></div>
            <div><label style={lblStyle}>Product Type</label>
              <select style={fldStyle} value={form.productType} onChange={e=>upd('productType',e.target.value)}>
                {PRODUCT_TYPES.map(t=><option key={t} value={t}>{PRODUCT_TYPE_ICONS[t]} {t}</option>)}
              </select>
            </div>
            <div><label style={lblStyle}>Category</label>
              <select style={fldStyle} value={form.category} onChange={e=>upd('category',e.target.value)}>
                {!categories.some(c => c.name === form.category) && form.category && <option value={form.category}>{form.category}</option>}
                {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom:14, maxWidth:260 }}>
            <label style={lblStyle}>Quantity In Stock (optional)</label>
            <input style={fldStyle} type="number" step="any" min="0" placeholder="Leave blank if not tracking inventory" value={form.quantity} onChange={e=>upd('quantity',e.target.value)} />
            <p style={{ fontSize:10, color:'#3a5a8a', marginTop:6 }}>
              In the same units this product's smallest price option is sold in (usually grams) — gets subtracted automatically on delivery, and the product auto-suspends once there's not enough left for even the smallest size.
            </p>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lblStyle}>Pricing Style</label>
            <div style={{ display:'flex', gap:10 }}>
              <button type="button" onClick={()=>setPricingStyle('options')}
                style={{ flex:1, padding:'9px 12px', borderRadius:9, cursor:'pointer', fontSize:12, fontFamily:'DM Sans,sans-serif',
                  background: pricingStyle==='options' ? '#0a1530' : '#0a0f20', color: pricingStyle==='options' ? '#1e6bff' : '#3a5a8a',
                  border:`1px solid ${pricingStyle==='options' ? '#1e3a6a' : '#0d1e3a'}` }}>
                Price options (sizes, packs, ml...)
              </button>
              <button type="button" onClick={()=>setPricingStyle('gram')}
                style={{ flex:1, padding:'9px 12px', borderRadius:9, cursor:'pointer', fontSize:12, fontFamily:'DM Sans,sans-serif',
                  background: pricingStyle==='gram' ? '#0a1530' : '#0a0f20', color: pricingStyle==='gram' ? '#1e6bff' : '#3a5a8a',
                  border:`1px solid ${pricingStyle==='gram' ? '#1e3a6a' : '#0d1e3a'}` }}>
                Bulk by the gram (slider)
              </button>
            </div>
          </div>

          {/* Discrete price options — works for any product type: sizes, pack
              counts, ml, whatever the product actually comes in */}
          {pricingStyle === 'options' && (
            <div style={{ marginBottom:14 }}>
              <label style={lblStyle}>Price Options</label>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {priceRows.map((row, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto', gap:8, alignItems:'center' }}>
                    <input type="text" placeholder={SIZE_HINTS[form.productType] || 'e.g. 3.5g'} value={row.size}
                      onChange={e=>updRow(i,'size',e.target.value)} style={{ ...fldStyle, padding:'8px 10px', fontSize:12 }} />
                    <input type="number" placeholder="coins" value={row.coins}
                      onChange={e=>updRow(i,'coins',e.target.value)} style={{ ...fldStyle, padding:'8px 10px', fontSize:12 }} />
                    <input type="number" placeholder="points (optional)" value={row.points}
                      onChange={e=>updRow(i,'points',e.target.value)} style={{ ...fldStyle, padding:'8px 10px', fontSize:12 }} />
                    <button type="button" onClick={()=>removeRow(i)} disabled={priceRows.length===1}
                      style={{ background:'none', border:'none', color: priceRows.length===1 ? '#1e3a6a' : '#c0101a', fontSize:18, cursor: priceRows.length===1 ? 'default' : 'pointer', padding:'0 6px' }}>×</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addRow} style={{ marginTop:8, background:'none', border:'1px dashed #1e3a6a', color:'#1e6bff', borderRadius:8, padding:'6px 12px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                + Add price option
              </button>
              <p style={{ fontSize:10, color:'#3a5a8a', marginTop:6 }}>Points is what this size costs to redeem with loyalty points — leave blank if it isn't points-redeemable.</p>
            </div>
          )}

          {/* Gram-based pricing */}
          {pricingStyle === 'gram' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14, maxWidth:400 }}>
              <div><label style={lblStyle}>Coins per Gram</label>
                <input type="number" placeholder="e.g. 2" value={gramCoins} onChange={e=>setGramCoins(e.target.value)} style={fldStyle} />
              </div>
              <div><label style={lblStyle}>Points per Gram (optional)</label>
                <input type="number" placeholder="e.g. 1" value={gramPoints} onChange={e=>setGramPoints(e.target.value)} style={fldStyle} />
              </div>
            </div>
          )}

          <div style={{ marginBottom:14 }}>
            <label style={lblStyle}>Product Photos (up to 4 — first one is the cover shown in the menu)</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, maxWidth:360 }}>
              {form.images.map((url, i) => (
                <div key={i} style={{ position:'relative' }}>
                  <div onClick={()=>imgRefs.current[i].click()} style={{ border:'1px dashed #1e3a6a', borderRadius:10, aspectRatio:'1', textAlign:'center', cursor:'pointer', background:'#0a0f20', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4, overflow:'hidden' }}>
                    {uploadingIdx === i ? <span style={{ fontSize:10, color:'#3a5a8a' }}>Uploading...</span>
                      : url ? <img src={url} alt={`preview ${i+1}`} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                      : <><span style={{ fontSize:18, color:'#1e3a6a' }}>📷</span><span style={{ fontSize:9, color:'#3a5a8a' }}>{i === 0 ? 'Cover' : `Photo ${i+1}`}</span></>}
                    <input ref={el => imgRefs.current[i] = el} type="file" accept="image/*" style={{ display:'none' }} onChange={e=>handleImg(i,e)} />
                  </div>
                  {url && (
                    <button type="button" onClick={()=>removeImg(i)} title="Remove this photo"
                      style={{ position:'absolute', top:-6, right:-6, width:18, height:18, borderRadius:'50%', background:'#c0101a', color:'#fff', border:'none', fontSize:12, lineHeight:'18px', cursor:'pointer', padding:0 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:14, maxWidth:600 }}>
            <label style={lblStyle}>Notes (optional)</label>
            <input style={fldStyle} type="text" placeholder="e.g. Limited stock..." value={form.notes} onChange={e=>upd('notes',e.target.value)} />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button type="submit" disabled={saving} style={{ background:'#c0101a', color:'#fff', border:'none', padding:'9px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>
              {saving ? (editingStrain ? 'Saving...' : 'Adding...') : (editingStrain ? 'Save Changes' : 'Add to Menu')}
            </button>
            {editingStrain && (
              <button type="button" onClick={resetForm} style={{ background:'none', border:'1px solid #1e3a6a', color:'#3a5a8a', padding:'9px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Current Menu */}
      {(() => {
        const activeProducts = menu.filter(m => m.active);
        const suspendedProducts = menu.filter(m => !m.active);
        const card = (item) => {
          // Low stock = can't cover even the smallest size this product
          // sells in — the same rule the server auto-suspends on, surfaced
          // here as a heads-up before it actually happens.
          const smallestSize = item.prices && item.prices.length
            ? Math.min(...item.prices.map(p => { const m = /^([\d.]+)\s*g/i.exec(String(p.size||'').trim()); return m ? parseFloat(m[1]) : 1; }))
            : 0;
          const lowStock = item.quantity !== null && item.quantity !== undefined && item.quantity < Math.max(smallestSize, 1);
          return (
          <div key={item.strain} style={{ background:'#06091a', border: lowStock ? '1px solid #4a3000' : '1px solid #0a1628', borderRadius:14, overflow:'hidden', opacity: item.active ? 1 : 0.6 }}>
            <div style={{ width:'100%', height:90, background:'#0a0f20', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
              {item.imageUrl ? <img src={item.imageUrl} alt={item.strain} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <span style={{ fontSize:32 }}>{PRODUCT_TYPE_ICONS[item.productType] || catIcons[item.category] || '🌿'}</span>}
            </div>
            <div style={{ padding:12 }}>
              <p style={{ fontSize:13, fontWeight:500, marginBottom:2 }}>{item.strain}</p>
              <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:4 }}>{item.productType || 'Flower'} • {item.category}</p>
              {item.prices && item.prices.map(p => (
                <span key={p.size} style={{ fontSize:11, color:'#1e6bff', marginRight:8 }}>
                  {p.size}: {p.coins}c{p.points > 0 ? ` / ${p.points}pt` : ''}
                </span>
              ))}
              {item.quantity !== null && item.quantity !== undefined && (
                <p style={{ fontSize:11, color: lowStock ? '#f0a020' : '#1a9e5a', marginTop:6 }}>
                  {lowStock ? '⚠ ' : ''}{item.quantity} in stock
                </p>
              )}
              {!item.active && <p style={{ fontSize:11, color:'#c0101a', marginTop:6 }}>Suspended — hidden from customers</p>}
              <div style={{ display:'flex', gap:6, marginTop:8 }}>
                <button className="btn-blue" onClick={()=>editProduct(item)} style={{ fontSize:11, padding:'4px 10px' }}>Edit</button>
                <button onClick={()=>toggleActive(item)} disabled={togglingStrain===item.strain}
                  className={item.active ? 'btn-danger' : 'btn-blue'} style={{ fontSize:11, padding:'4px 10px', opacity: togglingStrain===item.strain ? 0.6 : 1 }}>
                  {togglingStrain===item.strain ? 'Saving...' : item.active ? 'Suspend' : 'Reactivate'}
                </button>
              </div>
            </div>
          </div>
          );
        };

        return (
          <>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:12 }}>
              Current Menu <span style={{ fontSize:13, color:'#3a5a8a', fontFamily:'DM Sans,sans-serif', letterSpacing:0 }}>— {activeProducts.length} products live</span>
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom: suspendedProducts.length ? 24 : 0 }}>
              {activeProducts.map(card)}
            </div>

            {suspendedProducts.length > 0 && (
              <>
                <button onClick={()=>setShowSuspended(!showSuspended)} style={{ background:'none', border:'1px solid #0d1e3a', color:'#3a5a8a', borderRadius:9, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', marginBottom:12 }}>
                  {showSuspended ? 'Hide' : 'Show'} Suspended ({suspendedProducts.length})
                </button>
                {showSuspended && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                    {suspendedProducts.map(card)}
                  </div>
                )}
              </>
            )}
          </>
        );
      })()}
    </div>
  );
}

// ================================
// PROMOS TAB
// Time-boxed campaign announcements — the "live" ones (active + inside
// their date window) are what the customer app's banner shows.
// ================================
function PromosTab({ promos, onRefresh }) {
  const imgRef = useRef();
  const blankForm = { title:'', description:'', startDate:'', endDate:'', imageUrl:'', story:'', referralBonusPoints:'', referralBonusCoins:'', audience:['members'], goalType:'none', goalTarget:'', goalBonusCoins:'' };
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const toggleAudience = (a) => setForm(p => {
    const has = p.audience.includes(a);
    const next = has ? p.audience.filter(x => x !== a) : [...p.audience, a];
    // A goal only makes sense for the audience it's actually about — drop
    // it rather than leave a driver goal configured on a promo that no
    // longer targets drivers at all.
    const stillValidGoal = (p.goalType === 'driver_deliveries' && next.includes('drivers'))
      || (p.goalType === 'staff_signups' && next.includes('staff'));
    return { ...p, audience: next, goalType: stillValidGoal ? p.goalType : 'none', goalTarget: stillValidGoal ? p.goalTarget : '', goalBonusCoins: stillValidGoal ? p.goalBonusCoins : '' };
  });

  const upd = (f,v) => setForm(p=>({...p,[f]:v}));
  const fldStyle = { width:'100%', padding:'10px 12px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:9, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' };
  const lblStyle = { fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 };

  const handleImg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await axios.post('/api/promos/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      upd('imageUrl', r.data.url);
    } catch (err) { alert(err.response?.data?.error || 'Image upload failed'); }
    finally { setUploadingImg(false); }
  };

  const isLive = (p) => {
    if (!p.active) return false;
    const now = new Date();
    if (p.startDate && now < new Date(p.startDate)) return false;
    if (p.endDate && now > new Date(`${p.endDate}T23:59:59`)) return false;
    return true;
  };

  const createPromo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post('/api/promos', form);
      setForm(blankForm);
      onRefresh();
    } catch(e) { alert(e.response?.data?.error || 'Failed to create promo'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`/api/promos/${promo.id}/active`, { active: !promo.active });
      onRefresh();
    } catch(e) { alert('Failed to update promo'); }
  };

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Promos</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Time-boxed campaigns — live ones show as a banner in the customer app</p>

      {/* Create Promo Form */}
      <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:16, padding:20, marginBottom:24 }}>
        <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:16 }}>New Promo</p>
        <form onSubmit={createPromo}>
          <div style={{ marginBottom:14 }}>
            <label style={lblStyle}>Title</label>
            <input style={fldStyle} type="text" placeholder="e.g. Refer 2 friends this month" value={form.title} onChange={e=>upd('title',e.target.value)} required />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lblStyle}>Description</label>
            <input style={fldStyle} type="text" placeholder="e.g. Earn double points on every referral through Halloween" value={form.description} onChange={e=>upd('description',e.target.value)} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div><label style={lblStyle}>Start Date (optional)</label><input style={fldStyle} type="date" value={form.startDate} onChange={e=>upd('startDate',e.target.value)} /></div>
            <div><label style={lblStyle}>End Date (optional)</label><input style={fldStyle} type="date" value={form.endDate} onChange={e=>upd('endDate',e.target.value)} /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12, marginBottom:14 }}>
            <div>
              <label style={lblStyle}>Image</label>
              <div onClick={()=>imgRef.current.click()} style={{ border:'1px dashed #1e3a6a', borderRadius:10, padding:16, textAlign:'center', cursor:'pointer', background:'#0a0f20', minHeight:80, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6 }}>
                {uploadingImg ? <span style={{ fontSize:11, color:'#3a5a8a' }}>Uploading...</span>
                  : form.imageUrl ? <img src={form.imageUrl} alt="preview" style={{ width:60, height:60, objectFit:'cover', borderRadius:8 }} />
                  : <><span style={{ fontSize:24, color:'#1e3a6a' }}>📷</span><span style={{ fontSize:11, color:'#3a5a8a' }}>Tap to upload</span></>}
                <input ref={imgRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleImg} />
              </div>
            </div>
            <div>
              <label style={lblStyle}>Story (optional — full write-up behind the headline)</label>
              <textarea style={{ ...fldStyle, minHeight:80, resize:'vertical', fontFamily:'DM Sans,sans-serif' }}
                placeholder="The longer story, article, or culture piece behind this — shown when a customer taps 'Read more'"
                value={form.story} onChange={e=>upd('story',e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom:14, border:'1px solid #1e3a6a', borderRadius:10, padding:14 }}>
            <label style={lblStyle}>Referral Bonus Override (optional)</label>
            <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>
              While this promo is live, a referral payout uses these numbers instead of the usual {REFERRAL_REFERRER_POINTS} points / {REFERRAL_REFERRER_COINS} coins. Leave blank to use the normal amount.
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label style={lblStyle}>Points per referral</label>
                <input type="number" placeholder={String(REFERRAL_REFERRER_POINTS)} value={form.referralBonusPoints} onChange={e=>upd('referralBonusPoints',e.target.value)} style={fldStyle} />
              </div>
              <div><label style={lblStyle}>Coins per referral</label>
                <input type="number" placeholder={String(REFERRAL_REFERRER_COINS)} value={form.referralBonusCoins} onChange={e=>upd('referralBonusCoins',e.target.value)} style={fldStyle} />
              </div>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lblStyle}>Who Sees This</label>
            <div style={{ display:'flex', gap:10 }}>
              {[['members','👤 Members'],['drivers','🚗 Drivers'],['staff','📣 Staff']].map(([a,label]) => (
                <button key={a} type="button" onClick={()=>toggleAudience(a)}
                  style={{ flex:1, padding:'9px 12px', borderRadius:9, cursor:'pointer', fontSize:12, fontFamily:'DM Sans,sans-serif',
                    background: form.audience.includes(a) ? '#0a1530' : '#0a0f20', color: form.audience.includes(a) ? '#1e6bff' : '#3a5a8a',
                    border:`1px solid ${form.audience.includes(a) ? '#1e3a6a' : '#0d1e3a'}` }}>
                  {label}
                </button>
              ))}
            </div>
            {form.audience.length === 0 && <p style={{ fontSize:11, color:'#c0101a', marginTop:6 }}>Pick at least one audience or nobody will ever see this.</p>}
          </div>

          {/* A goal turns a driver/staff promo from a plain announcement
              into something that actually tracks real activity and pays
              out the moment someone hits it — see utils/promoGoals.js. */}
          {(form.audience.includes('drivers') || form.audience.includes('staff')) && (
            <div style={{ marginBottom:14, border:'1px solid #1e3a6a', borderRadius:10, padding:14 }}>
              <label style={lblStyle}>Trackable Goal (optional)</label>
              <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>
                Leave as "Just an announcement" for something informational only. Pick a real goal and it tracks automatically — hitting the target pays the bonus right away, no manual verification.
              </p>
              <select style={{ ...fldStyle, marginBottom: form.goalType === 'none' ? 0 : 10 }} value={form.goalType} onChange={e=>upd('goalType', e.target.value)}>
                <option value="none">Just an announcement</option>
                {form.audience.includes('drivers') && <option value="driver_deliveries">Driver: complete N deliveries</option>}
                {form.audience.includes('staff') && <option value="staff_signups">Staff: sign up N people</option>}
              </select>
              {form.goalType !== 'none' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div><label style={lblStyle}>Target ({form.goalType === 'driver_deliveries' ? 'deliveries' : 'signups'})</label>
                    <input type="number" min="1" placeholder="e.g. 10" value={form.goalTarget} onChange={e=>upd('goalTarget',e.target.value)} style={fldStyle} required />
                  </div>
                  <div><label style={lblStyle}>Bonus Coins On Completion</label>
                    <input type="number" min="1" placeholder="e.g. 50" value={form.goalBonusCoins} onChange={e=>upd('goalBonusCoins',e.target.value)} style={fldStyle} required />
                  </div>
                </div>
              )}
            </div>
          )}

          <button type="submit" disabled={saving || !form.title || uploadingImg || form.audience.length === 0} style={{ background:'#c0101a', color:'#fff', border:'none', padding:'9px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>
            {saving ? 'Creating...' : 'Create Promo'}
          </button>
        </form>
      </div>

      {/* Promo List */}
      <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:12 }}>
        All Promos <span style={{ fontSize:13, color:'#3a5a8a', fontFamily:'DM Sans,sans-serif', letterSpacing:0 }}>— {promos.length} total</span>
      </p>
      {promos.length === 0 && <p style={{ fontSize:13, color:'#3a5a8a' }}>No promos yet — create one above.</p>}
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {promos.map(p => {
          const live = isLive(p);
          return (
            <div key={p.id} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, display:'flex', justifyContent:'space-between', alignItems:'center', gap:16 }}>
              {p.imageUrl && <img src={p.imageUrl} alt="" style={{ width:56, height:56, objectFit:'cover', borderRadius:10, flexShrink:0 }} />}
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <p style={{ fontSize:14, color:'#e8e8e8', fontWeight:500 }}>{p.title}</p>
                  {live && <span style={{ fontSize:10, color:'#1a9e5a', background:'#0a2010', border:'1px solid #0a3020', borderRadius:20, padding:'2px 8px', textTransform:'uppercase', letterSpacing:1 }}>Live</span>}
                  {p.story && <span style={{ fontSize:10, color:'#3a5a8a', border:'1px solid #0d1e3a', borderRadius:20, padding:'2px 8px', textTransform:'uppercase', letterSpacing:1 }}>Has story</span>}
                  {(p.referralBonusPoints > 0 || p.referralBonusCoins > 0) && (
                    <span style={{ fontSize:10, color:'#f0a020', border:'1px solid #3a2a05', borderRadius:20, padding:'2px 8px', textTransform:'uppercase', letterSpacing:1 }}>
                      {p.referralBonusPoints || REFERRAL_REFERRER_POINTS}pt / {p.referralBonusCoins || REFERRAL_REFERRER_COINS}c referral
                    </span>
                  )}
                </div>
                {p.description && <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:4 }}>{p.description}</p>}
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap' }}>
                  {(p.audience || ['members']).map(a => (
                    <span key={a} style={{ fontSize:9, color:'#1e6bff', background:'#0a1530', border:'1px solid #1e3a6a', borderRadius:20, padding:'1px 7px', textTransform:'capitalize' }}>
                      {{ members:'👤', drivers:'🚗', staff:'📣' }[a]} {a}
                    </span>
                  ))}
                  {p.goalType && p.goalType !== 'none' && (
                    <span style={{ fontSize:9, color:'#1a9e5a', background:'#0a2010', border:'1px solid #0a3020', borderRadius:20, padding:'1px 7px' }}>
                      🎯 {p.goalTarget} {p.goalType === 'driver_deliveries' ? 'deliveries' : 'signups'} → +{p.goalBonusCoins}c
                    </span>
                  )}
                </div>
                <p style={{ fontSize:11, color:'#1e3a6a' }}>
                  {p.startDate || p.endDate ? `${p.startDate || 'anytime'} → ${p.endDate || 'ongoing'}` : 'No date window — runs whenever active'}
                </p>
              </div>
              <button onClick={() => toggleActive(p)}
                style={{
                  background: p.active ? '#0a2010' : '#1a0505', color: p.active ? '#1a9e5a' : '#c0101a',
                  border: `1px solid ${p.active ? '#0a3020' : '#3a0a0a'}`, borderRadius:20, padding:'8px 18px',
                  fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500, minWidth:70, flexShrink:0
                }}>
                {p.active ? 'ON' : 'OFF'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ================================
// MESSAGES TAB
// The admin inbox — every standing thread with a member or driver, whoever
// started it. A customer tapping "Contact Support" with no prior admin
// message shows up here same as anyone admin messaged first.
// ================================
function MessagesTab({ threads, onRefresh }) {
  const [openThread, setOpenThread] = useState(null); // { role, id, name }

  const closeThread = () => {
    setOpenThread(null);
    onRefresh(); // opening a thread marks it read server-side — reflect that here
  };

  const roleIcons = { customer:'👤', driver:'🚗', staff:'📣' };

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Messages</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Every standing thread with a member or driver — including ones they started</p>

      {threads.length === 0 && <p style={{ fontSize:13, color:'#3a5a8a' }}>No conversations yet.</p>}

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {threads.map(t => (
          <div key={t.threadId} onClick={() => setOpenThread(t)}
            style={{
              display:'flex', justifyContent:'space-between', alignItems:'center', gap:12,
              background: t.unreadCount > 0 ? '#0a0f20' : '#06091a',
              border:`1px solid ${t.unreadCount > 0 ? '#1e3a6a' : '#0a1628'}`,
              borderRadius:14, padding:'14px 16px', cursor:'pointer'
            }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:0 }}>
              <span style={{ fontSize:20 }}>{roleIcons[t.role] || '👤'}</span>
              <div style={{ minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <p style={{ fontSize:14, color:'#e8e8e8', fontWeight: t.unreadCount > 0 ? 600 : 400 }}>{t.name}</p>
                  <span style={{ fontSize:9, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1 }}>{t.role}</span>
                </div>
                <p style={{ fontSize:12, color:'#3a5a8a', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:420 }}>
                  {t.lastFrom === 'admin' ? 'You: ' : ''}{t.lastMessage}
                </p>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
              <span style={{ fontSize:10, color:'#1e3a6a' }}>{new Date(t.lastAt).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
              {t.unreadCount > 0 && (
                <span style={{ background:'#c0101a', color:'#fff', borderRadius:'50%', width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11 }}>
                  {t.unreadCount}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {openThread && (
        <ChatThread
          title={`Message ${openThread.name}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/${openThread.role}/${encodeURIComponent(openThread.id)}`}
          postUrl={`/api/messages/thread/${openThread.role}/${encodeURIComponent(openThread.id)}`}
          onClose={closeThread}
        />
      )}
    </div>
  );
}

// ================================
// MEMBERS TAB
// ================================
function MembersTab({ members, orders, staff, drivers, pending, onApprove, onDeny, onTopUp, onAwardPoints, onSetStatus, onRefresh }) {
  const [topUp, setTopUp]   = useState({});
  const [topUpDollar, setTopUpDollar] = useState({});
  const [pointsAward, setPointsAward] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addCardMid, setAddCardMid] = useState(''); // '' = auto-assign next available, same as before this existed
  const [unclaimedCards, setUnclaimedCards] = useState([]); // full current inventory — not just what was last generated
  const [showCards, setShowCards] = useState(false);
  const [genCount, setGenCount] = useState(20);
  const [generating, setGenerating] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const [profileTarget, setProfileTarget] = useState(null); // member whose profile/order history is open
  const [staffProfileTarget, setStaffProfileTarget] = useState(null); // staffer opened from a member's "signed up by" link
  const [rosterSearch, setRosterSearch] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const cardGridRef = useRef(null);
  const memberQrGridRef = useRef(null);

  useEffect(() => {
    fetchUnclaimed();
    axios.get('/api/settings/support-phone').then(r => setSupportPhone(r.data.phone || '')).catch(()=>{});
  }, []);

  async function fetchUnclaimed() {
    try {
      const r = await axios.get('/api/members/cards/unclaimed');
      setUnclaimedCards(r.data.cards.map(c => c.mid));
    } catch (e) {}
  }

  async function generateCards() {
    setGenerating(true);
    try {
      await axios.post('/api/members/cards/generate', { count: parseInt(genCount, 10) });
      await fetchUnclaimed();
      setShowCards(true);
    } catch (e) { alert(e.response?.data?.error || 'Failed to generate cards'); }
    finally { setGenerating(false); }
  }

  // Shared by printCards() (a batch of blank/unclaimed cards) and
  // reprintCard() (one card for an already-active member who lost
  // theirs) — same physical card template either way, just a different
  // source for which MID(s) go on it.
  function openFrontCardPrintWindow(cardsHtml) {
    const win = window.open('', '_blank');
    if (!win) {
      alert('Your browser blocked the print window. Allow popups for this site and try again.');
      return;
    }
    win.document.write(`
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Weedo World — Member Cards</title>
          <style>
            /* Sized for direct-to-card printers (e.g. Magicard 300) — one
               CR80 card (3.375in x 2.125in, landscape) per printed "page",
               not a paper grid meant for cutting. In the print dialog,
               select the card printer and use 100%/Actual Size, not
               "Fit to page" — the printer's own media size handles that. */
            @page { size: 3.375in 2.125in; margin: 0; }
            /* Chrome drops CSS backgrounds from print output unless the
               print dialog's "Background graphics" box is checked — force
               it instead, so the card art prints even if that's missed. */
            html { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
            body { margin: 0; font-family: 'DM Sans', Arial, sans-serif; background: #fff; }
            .print-card {
              width: 3.375in; height: 2.125in;
              position: relative;
              box-sizing: border-box;
              page-break-after: always;
              -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
            }
            /* The art was designed to look good backlit on a screen — on a
               direct-to-card dye-sub printer (reflective, no backlight, and
               a ribbon with much less shadow range than a monitor) the same
               file comes out noticeably darker, crushing the navy
               background toward solid black. Brightening on a layer behind
               the content (not a filter on .print-card itself) compensates
               for that without touching the QR code or text zones drawn on
               top, which need to stay at full contrast. */
            .print-card-bg {
              position: absolute; inset: 0;
              background: url('${window.location.origin}/images/card-bg.jpg') center/cover no-repeat;
              /* brightness() alone barely touches near-black pixels (it's a
                 straight multiply — 0 stays 0); contrast() below 1 pulls
                 everything toward mid-grey instead, which is what actually
                 lifts the navy panels. Tuned against a real printed card
                 that came out almost solid black — may still need another
                 pass once this prints. */
              filter: brightness(1.3) contrast(0.8) saturate(1.3);
              -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
            }
            .print-card:last-child { page-break-after: auto; }
            .card-qr-zone {
              position: absolute; left: 38%; top: 30%; width: 25%; height: 40%;
              display: flex; align-items: center; justify-content: center;
            }
            .card-qr-zone svg { width: 0.88in; height: 0.88in; }
            .card-activate-zone {
              position: absolute; left: 20%; top: 71%; width: 60%; height: 10%;
              display: flex; align-items: center; justify-content: center;
              color: #fff; font-size: 8px; font-weight: 700; letter-spacing: 1.5px;
              text-transform: uppercase; text-shadow: 0 1px 3px rgba(0,0,0,.9);
            }
            .card-mid-zone {
              position: absolute; left: 20%; top: 84.5%; width: 60%; height: 8%;
              display: flex; align-items: center; justify-content: center;
              font-weight: 700; letter-spacing: 1.5px; font-size: 12px; color: #fff;
              text-shadow: 0 1px 3px rgba(0,0,0,.9);
            }
          </style>
        </head>
        <body>
          ${cardsHtml}
        </body>
      </html>
    `);
    win.document.close();
    // onload after document.write() fires inconsistently across browsers —
    // a short delay to let layout settle is more reliable here.
    setTimeout(() => { win.focus(); win.print(); }, 300);
  }

  // Card background art (astronaut/earth/W, same identity as the rest of
  // the app) has the logo baked in, so there's no separate code-rendered
  // logo zone anymore — just a QR zone, a short activation cue, and the
  // M.I.D., all positioned (in % of the card) to land inside the art's own
  // reserved dark panels. Positions were tuned by eye against the real
  // artwork, not computed — if the background art ever changes, these
  // percentages likely need re-tuning too.
  function printCards() {
    if (!cardGridRef.current) return;
    const svgNodes = cardGridRef.current.querySelectorAll('svg');
    const cardsHtml = unclaimedCards.map((mid, i) => `
      <div class="print-card">
        <div class="print-card-bg"></div>
        <div class="card-qr-zone">${svgNodes[i] ? svgNodes[i].outerHTML : ''}</div>
        <div class="card-activate-zone">Scan to Activate</div>
        <div class="card-mid-zone">M.I.D. ${mid}</div>
      </div>
    `).join('');
    openFrontCardPrintWindow(cardsHtml);
  }

  // One-off reprint for an existing, already-activated member who lost
  // their card — same front template as printCards(), just sourced from
  // the hidden per-member QR grid below instead of the unclaimed pool.
  // The MID itself is permanent and never invalidated by printing, so
  // this is safe to run as many times as needed.
  function reprintCard(mid) {
    if (!memberQrGridRef.current) return;
    const svgEl = memberQrGridRef.current.querySelector(`[data-mid="${mid}"] svg`);
    const cardHtml = `
      <div class="print-card">
        <div class="print-card-bg"></div>
        <div class="card-qr-zone">${svgEl ? svgEl.outerHTML : ''}</div>
        <div class="card-activate-zone">Scan to Activate</div>
        <div class="card-mid-zone">M.I.D. ${mid}</div>
      </div>
    `;
    openFrontCardPrintWindow(cardHtml);
  }

  // Printed as its own batch (not alternated with fronts) — the Magicard
  // 300 is single-sided, so the real workflow is: print fronts, flip the
  // stack, feed it back through, print backs. Same card count as the
  // front batch so the two stacks line up card-for-card. No background
  // art exists for the back yet, so this is CSS-only in brand colors —
  // swap in a background image the same way the front's card-bg.jpg
  // works, once real artwork exists.
  function printCardBacks() {
    const phoneLine = supportPhone ? `, or call ${supportPhone}` : '';
    const cardHtml = `
      <div class="print-card-back">
        <img class="back-badge" src="${window.location.origin}/images/card-back-badge.jpg" alt="" />
        <div class="back-top">
          <p class="back-logo">WEEDO <span>WORLD</span></p>
          <p class="back-heading">How It Works</p>
          <ol class="back-steps">
            <li>Open your wallet in the app</li>
            <li>Browse the menu, order with coins</li>
            <li>Track your driver live</li>
            <li>Confirm delivery — card scan or wallet code</li>
          </ol>
        </div>
        <p class="back-heading">Reloading</p>
        <p class="back-text">Add coins anytime in your wallet. Approval isn't instant.</p>
        <p class="back-heading">Need Help?</p>
        <p class="back-text">Message us in the app${phoneLine}</p>
        <p class="back-fine-print">Private member access only. Must be 21+. Card is non-transferable.</p>
      </div>
    `;
    const cardsHtml = unclaimedCards.map(() => cardHtml).join('');

    const win = window.open('', '_blank');
    if (!win) {
      alert('Your browser blocked the print window. Allow popups for this site and try again.');
      return;
    }
    win.document.write(`
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Weedo World — Member Card Backs</title>
          <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500&display=swap" />
          <style>
            @page { size: 3.375in 2.125in; margin: 0; }
            html { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
            body { margin: 0; font-family: 'DM Sans', Arial, sans-serif; background: #fff; }
            .print-card-back {
              width: 3.375in; height: 2.125in;
              position: relative;
              box-sizing: border-box;
              page-break-after: always;
              background: #cfe9ff;
              color: #0a1a33;
              padding: 0.09in 0.16in;
              -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;
            }
            .print-card-back:last-child { page-break-after: auto; }
            .back-badge {
              position: absolute; top: 0.08in; right: 0.1in;
              width: 0.75in; height: 0.98in;
              border-radius: 7px; object-fit: cover;
              border: 2px solid #ff3b3b;
            }
            .back-top { max-width: calc(100% - 0.93in); }
            .back-logo { font-family:'Bebas Neue',sans-serif; font-size:14px; letter-spacing:1px; color:#0a1a33; margin:0 0 6px; }
            .back-logo span { color:#ff3b3b; margin-left:5px; }
            .back-heading { font-family:'Bebas Neue',sans-serif; font-size:10px; letter-spacing:1.5px; text-transform:uppercase; color:#ff3b3b; margin:6px 0 2px; }
            .back-steps { margin:0; padding-left:11px; font-size:9.5px; line-height:1.45; color:#0a1a33; }
            .back-text { margin:0; font-size:9.5px; line-height:1.3; color:#0a1a33; }
            .back-fine-print { position:absolute; bottom:0.07in; left:0.16in; right:0.16in; font-size:6.5px; color:#3c6690; line-height:1.25; }
          </style>
        </head>
        <body>
          ${cardsHtml}
        </body>
      </html>
    `);
    win.document.close();
    // A blank document.write()'d window doesn't inherit the main app's fonts
    // and doesn't wait for its own images either — both the Bebas Neue/DM
    // Sans link and the astronaut badge photo have to actually finish
    // loading before print(), or the browser prints whatever's rendered at
    // that instant: a wider fallback font, or just the badge's red border
    // with no photo inside it yet because the image request hadn't
    // resolved. (One print() per card batch, not per card — images repeat
    // across cards as the same cached request, so this only waits once.)
    let printed = false;
    const doPrint = () => { if (printed) return; printed = true; win.focus(); win.print(); };
    const fontsReady = (win.document.fonts && win.document.fonts.ready) ? win.document.fonts.ready.catch(() => {}) : Promise.resolve();
    const imagesReady = Promise.all(Array.from(win.document.images).map(img => img.complete ? Promise.resolve() : new Promise(resolve => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    })));
    Promise.all([fontsReady, imagesReady]).then(doPrint);
    setTimeout(doPrint, 2500); // safety net if a font or image load hangs
  }

  const addMember = async (payload) => {
    setAdding(true);
    try {
      const r = await axios.post('/api/members/approve', { ...payload, cardMid: addCardMid || undefined });
      alert(`✅ Member added!\nHand them the card for M.I.D. ${r.data.mid} — they'll set their own PIN when they activate it.`);
      setShowAdd(false);
      setAddCardMid('');
      onRefresh();
      fetchUnclaimed();
    } catch(e) { alert(e.response?.data?.error || 'Failed to add member'); }
    finally { setAdding(false); }
  };

  const fldStyle = { width:'100%', padding:'10px 12px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:9, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' };
  const lblStyle = { fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 };

  return (
    <div>
      {/* Off-screen QR codes for every active member, used by reprintCard()
         — kept mounted at all times so a reprint never has to wait on a
         render before it can grab the SVG markup. */}
      <div ref={memberQrGridRef} style={{ position:'absolute', left:-9999, top:-9999 }} aria-hidden="true">
        {members.map(m => (
          <div key={m.mid} data-mid={m.mid}>
            <QRCodeSVG value={`${window.location.origin}/c/${m.mid}`} size={90} />
          </div>
        ))}
      </div>

      {/* Blank Card Inventory */}
      <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1 }}>🪪 Blank Card Inventory</p>
            <p style={{ fontSize:12, color:'#3a5a8a', marginTop:2 }}>
              Pre-printed cards ready to hand to new members. New approvals pull from this pool — no SMS involved.
            </p>
          </div>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:30, color: unclaimedCards.length === 0 ? '#c0101a' : '#1a9e5a', letterSpacing:1 }}>
            {unclaimedCards.length}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:12 }}>
          <input type="number" min="1" max="200" value={genCount} onChange={e=>setGenCount(e.target.value)}
            style={{ width:80, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, fontFamily:'DM Sans,sans-serif' }} />
          <button onClick={generateCards} disabled={generating} style={{ background:'#1e3a8a', color:'#fff', border:'none', padding:'8px 16px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            {generating ? 'Generating...' : `Generate ${genCount} cards`}
          </button>
          {unclaimedCards.length > 0 && (
            <button onClick={() => setShowCards(s => !s)} style={{ background:'transparent', color:'#1e6bff', border:'1px solid #1e3a6a', padding:'8px 16px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
              {showCards ? 'Hide cards' : `View all ${unclaimedCards.length} cards`}
            </button>
          )}
        </div>

        {showCards && unclaimedCards.length > 0 && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #0a1628' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <p style={{ fontSize:12, color:'#3a5a8a' }}>
                {unclaimedCards.length} unclaimed card{unclaimedCards.length !== 1 ? 's' : ''} — this is your full current inventory, not just what you just generated.
              </p>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={printCards} style={{ background:'#1e3a8a', color:'#fff', border:'none', padding:'6px 14px', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  🖨️ Print Fronts
                </button>
                <button onClick={printCardBacks} style={{ background:'transparent', color:'#1e6bff', border:'1px solid #1e3a6a', padding:'6px 14px', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  🖨️ Print Backs
                </button>
              </div>
            </div>
            <div ref={cardGridRef} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px, 1fr))', gap:12 }}>
              {unclaimedCards.map(mid => (
                <div key={mid} style={{ background:'#fff', borderRadius:10, padding:10, textAlign:'center' }}>
                  <QRCodeSVG value={`${window.location.origin}/c/${mid}`} size={90} />
                  <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:14, color:'#0a0a0a', marginTop:6, letterSpacing:1 }}>{mid}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Members</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Manage your private roster and coin wallets</p>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Total Members', val:members.length, color:'#e8e8e8' },
          { label:'Total Coins Held', val:members.reduce((s,m)=>s+parseFloat(m.wallet||0),0).toFixed(0), color:'#1e6bff' },
          { label:'Total Points Held', val:members.reduce((s,m)=>s+parseFloat(m.points||0),0).toFixed(0), color:'#f0a020' },
          { label:'Pending Approvals', val:pending.length, color:pending.length>0?'#f0a020':'#e8e8e8' },
        ].map(m=>(
          <div key={m.label} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
            <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{m.label}</p>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, color:m.color, letterSpacing:1 }}>{m.val}</p>
          </div>
        ))}
      </div>

      {/* Pending Requests */}
      {pending.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, color:'#f0a020', marginBottom:12 }}>⏳ Pending Approvals</p>
          {pending.map((r,i) => <PendingRequestCard key={i} r={r} onApprove={onApprove} onDeny={onDeny} />)}
        </div>
      )}

      {/* Add Member Manually */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, gap:12, flexWrap:'wrap' }}>
        <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1 }}>Member Roster</p>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input value={rosterSearch} onChange={e=>setRosterSearch(e.target.value)} placeholder="Search members — name, M.I.D., phone..."
            style={{ background:'#0a0f20', border:'1px solid #0d1e3a', color:'#e8e8e8', borderRadius:8, padding:'6px 12px', fontSize:12, fontFamily:'DM Sans,sans-serif', width:260 }} />
          <button onClick={()=>setShowAdd(!showAdd)} style={{ background:'#c0101a', color:'#fff', border:'none', padding:'7px 14px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            {showAdd ? 'Cancel' : '+ Add Member'}
          </button>
        </div>
      </div>

      {showAdd && (
        <div style={{ background:'#06091a', border:'1px solid #1e3a6a', borderRadius:14, padding:20, marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:16, letterSpacing:1, marginBottom:14 }}>Add Member Manually</p>

          <label style={lblStyle}>Card to assign</label>
          <select value={addCardMid} onChange={e=>setAddCardMid(e.target.value)} style={{ ...fldStyle, marginBottom:16 }}>
            <option value="">Auto-assign next available card</option>
            {unclaimedCards.map(mid => <option key={mid} value={mid}>M.I.D. {mid}</option>)}
          </select>

          <MemberSignupForm onSubmit={addMember} submitting={adding} submitLabel="Add Member + Generate MID" />
        </div>
      )}

      {/* Members Table */}
      {(() => {
        const q = rosterSearch.trim().toLowerCase();
        const visibleMembers = q ? members.filter(m => [m.name, m.mid, m.phone].some(f => String(f||'').toLowerCase().includes(q))) : members;
        return (
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr>
          {['M.I.D.','Name','Phone','Zip','Wallet (coins)','Add Coins','Points','Award Points','Status','Actions'].map(h=>(
            <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'8px 12px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
          ))}
        </tr></thead>
        {q && visibleMembers.length === 0 && (
          <tbody><tr><td colSpan={10} style={{ padding:'16px 12px', fontSize:12, color:'#3a5a8a' }}>No members match "{rosterSearch}".</td></tr></tbody>
        )}
        <tbody>
          {visibleMembers.map(m=>(
            <tr key={m.mid} onMouseEnter={e=>e.currentTarget.style.background='#06091a'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <td style={{ padding:'10px 12px' }}>
                <span style={{ background:'#0a1530', color:'#1e6bff', borderRadius:6, padding:'2px 8px', fontSize:11, fontFamily:'Bebas Neue,sans-serif', letterSpacing:1 }}>{m.mid}</span>
              </td>
              <td style={{ padding:'10px 12px' }}>
                <button onClick={()=>setProfileTarget(m)} style={{ background:'none', border:'none', padding:0, color:'#e8e8e8', cursor:'pointer', textDecoration:'underline', textDecorationColor:'#1e3a6a', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>{m.name}</button>
              </td>
              <td style={{ padding:'10px 12px', color:'#e8e8e8' }}>{m.phone}</td>
              <td style={{ padding:'10px 12px', color:'#e8e8e8' }}>{m.zip}</td>
              <td style={{ padding:'10px 12px', fontFamily:'Bebas Neue,sans-serif', fontSize:15, color:'#1a9e5a' }}>
                {parseFloat(m.wallet||0).toFixed(0)} coins
              </td>
              <td style={{ padding:'10px 12px' }}>
                <div style={{ display:'flex', gap:6 }}>
                  <input type="number" placeholder="coins" title="Coins to add" style={{ width:60, padding:'4px 8px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:7, color:'#e8e8e8', fontSize:12, fontFamily:'DM Sans,sans-serif' }}
                    value={topUp[m.mid]||''} onChange={e=>setTopUp(p=>({...p,[m.mid]:e.target.value}))} />
                  <input type="number" placeholder="$ (opt.)" title="Actual dollars collected — leave blank to estimate from the daily rate" style={{ width:65, padding:'4px 8px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:7, color:'#e8e8e8', fontSize:12, fontFamily:'DM Sans,sans-serif' }}
                    value={topUpDollar[m.mid]||''} onChange={e=>setTopUpDollar(p=>({...p,[m.mid]:e.target.value}))} />
                  <button onClick={()=>{ if(topUp[m.mid]){ onTopUp(m.mid,topUp[m.mid],topUpDollar[m.mid]); setTopUp(p=>({...p,[m.mid]:''})); setTopUpDollar(p=>({...p,[m.mid]:''})); }}}
                    style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    Add
                  </button>
                </div>
              </td>
              <td style={{ padding:'10px 12px' }}>
                <span style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:15, color:'#f0a020' }}>{parseFloat(m.points||0).toFixed(0)}</span>
                {tierForPoints(parseFloat(m.points||0)) && (
                  <span style={{
                    marginLeft:7, fontSize:9, color: tierForPoints(parseFloat(m.points||0)).color,
                    border:`1px solid ${tierForPoints(parseFloat(m.points||0)).color}40`, borderRadius:20,
                    padding:'2px 7px', textTransform:'uppercase', letterSpacing:0.5
                  }}>
                    {tierForPoints(parseFloat(m.points||0)).name}
                  </span>
                )}
              </td>
              <td style={{ padding:'10px 12px' }}>
                <div style={{ display:'flex', gap:6 }}>
                  <input type="number" placeholder="points" style={{ width:70, padding:'4px 8px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:7, color:'#e8e8e8', fontSize:12, fontFamily:'DM Sans,sans-serif' }}
                    value={pointsAward[m.mid]||''} onChange={e=>setPointsAward(p=>({...p,[m.mid]:e.target.value}))} />
                  <button onClick={()=>{ if(pointsAward[m.mid]){ onAwardPoints(m.mid,pointsAward[m.mid]); setPointsAward(p=>({...p,[m.mid]:''})); }}}
                    style={{ background:'#1a1205', color:'#f0a020', border:'1px solid #3a2a05', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    Add
                  </button>
                </div>
              </td>
              <td style={{ padding:'10px 12px' }}>
                <span style={{ fontSize:11, color: m.status === 'suspended' ? '#c0101a' : '#1a9e5a' }}>
                  ● {m.status === 'suspended' ? 'Suspended' : 'Active'}
                </span>
              </td>
              <td style={{ padding:'10px 12px' }}>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={()=>setChatTarget({ role:'customer', id:m.mid, label:m.name })} style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>💬 Message</button>
                  <button onClick={()=>reprintCard(m.mid)} title="Reprint this member's card — same MID, safe to run as many times as needed" style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>🖨️ Reprint Card</button>
                  {m.status === 'suspended' ? (
                    <button onClick={()=>onSetStatus(m.mid,'active')} style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Reactivate</button>
                  ) : (
                    <button onClick={()=>onSetStatus(m.mid,'suspended')} style={{ background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Suspend</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
        );
      })()}

      {chatTarget && (
        <ChatThread
          title={`Message ${chatTarget.label}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          postUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          onClose={() => setChatTarget(null)}
        />
      )}

      {profileTarget && (
        <MemberProfileModal
          member={profileTarget}
          orders={orders}
          members={members}
          staff={staff}
          drivers={drivers}
          onSelectMember={setProfileTarget}
          onSelectStaff={(username) => setStaffProfileTarget(staff.find(s => s.username === username))}
          onClose={() => setProfileTarget(null)}
        />
      )}

      {staffProfileTarget && (
        <StaffProfileModal staffMember={staffProfileTarget} members={members} orders={orders} drivers={drivers} staff={staff} onClose={()=>setStaffProfileTarget(null)} />
      )}
    </div>
  );
}

// ================================
// MEMBER PROFILE — the roster only ever showed a row of actions; this is
// the first place an admin can see one member's full picture in one view:
// their account details plus every order they've ever placed, any status.
// orders is already the full /api/orders/all history the dashboard holds
// for Dispatch, just filtered here by mid — no extra API call needed.
// ================================
function MemberProfileModal({ member, orders, members, staff, drivers, onSelectMember, onSelectStaff, onClose }) {
  const history = orders.filter(o => String(o.mid).trim() === String(member.mid).trim());
  const statusColor = (s) => s === 'Delivered' ? '#1a9e5a' : s === 'Attempted Delivery' ? '#3a5a8a' : '#f0a020';
  const [detailOrder, setDetailOrder] = useState(null); // order row whose transaction detail is open
  const points = parseFloat(member.points || 0);
  const tier = tierForPoints(points); // shared top-level helper — see POINT_TIERS
  const referrer = member.referredBy ? members?.find(m => String(m.mid) === String(member.referredBy)) : null;
  const signedUpByStaffer = member.signedUpByStaff ? staff?.find(s => s.username === member.signedUpByStaff) : null;
  const linkStyle = { background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#1e6bff', textDecoration:'underline', textDecorationColor:'#1e3a6a' };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:620, maxHeight:'85vh', background:'#06091a', border:'1px solid #1e3a6a', borderRadius:16, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #0a1628' }}>
          <div>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:1, color:'#e8e8e8' }}>{member.name}</p>
            <p style={{ fontSize:11, color:'#3a5a8a' }}>M.I.D. {member.mid} • {member.status === 'suspended' ? 'Suspended' : 'Active'}</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ padding:'16px 20px', borderBottom:'1px solid #0a1628', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, fontSize:12 }}>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Phone</p><p style={{ color:'#e8e8e8' }}>{member.phone || '—'}</p></div>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>ZIP</p><p style={{ color:'#e8e8e8' }}>{member.zip || '—'}</p></div>
          <div style={{ gridColumn:'1 / -1' }}><p style={{ color:'#3a5a8a', marginBottom:2 }}>Address</p><p style={{ color:'#e8e8e8' }}>{member.address || '—'}</p></div>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Wallet</p><p style={{ fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a', fontSize:15 }}>{parseFloat(member.wallet||0).toFixed(0)} coins</p></div>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Points</p><p style={{ fontFamily:'Bebas Neue,sans-serif', color:'#1e6bff', fontSize:15 }}>{points.toFixed(0)}{tier && <span style={{ fontFamily:'DM Sans,sans-serif', fontSize:11, color: tier.color, marginLeft:6 }}>• {tier.name}</span>}</p></div>
          {/* How this member entered the network — a peer referral (another
              member) or a staff signup, never both. Each name is clickable,
              same cross-navigation the Staff profile's "members signed up"
              list offers in reverse. */}
          {referrer && (
            <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Referred by</p><p><button onClick={()=>onSelectMember(referrer)} style={linkStyle}>{referrer.name} ({referrer.mid})</button></p></div>
          )}
          {signedUpByStaffer && (
            <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Signed up by</p><p><button onClick={()=>onSelectStaff(member.signedUpByStaff)} style={linkStyle}>{signedUpByStaffer.name} (@{signedUpByStaffer.username})</button></p></div>
          )}
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px' }}>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Order History — {history.length} order{history.length === 1 ? '' : 's'}</p>
          {history.length === 0 ? (
            <p style={{ fontSize:12, color:'#3a5a8a' }}>No orders yet.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>
                {['OID','Summary','Total','Status','Placed'].map(h => (
                  <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'6px 8px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {history.map(o => (
                  <tr key={o.oid}>
                    <td style={{ padding:'8px' }}>
                      <button onClick={()=>setDetailOrder(o)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'Bebas Neue,sans-serif', color:'#1e6bff', letterSpacing:1, fontSize:12, textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.oid}</button>
                    </td>
                    <td style={{ padding:'8px', color:'#888', maxWidth:160 }}>{o.summary}</td>
                    <td style={{ padding:'8px', fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a' }}>{o.total}</td>
                    <td style={{ padding:'8px', color:statusColor(o.status) }}>{o.status || 'Pending'}</td>
                    <td style={{ padding:'8px', color:'#3a5a8a' }}>{o.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detailOrder && <OrderDetailModal order={detailOrder} drivers={drivers} orders={orders} onClose={()=>setDetailOrder(null)} />}
    </div>
  );
}

// ================================
// AGE VERIFICATION PHOTO — fetched with auth (an <img> tag can't send a
// Bearer token on its own), rendered as an object URL, click to view full size.
// ================================
function AdminPhoto({ phone, type, label }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    axios.get(`/api/members/pending/${encodeURIComponent(phone)}/photo/${type}`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [phone, type]);

  return (
    <div style={{ flex:1 }}>
      <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</p>
      {url ? (
        <img src={url} alt={label} onClick={()=>window.open(url,'_blank')}
          style={{ width:'100%', height:150, objectFit:'cover', borderRadius:8, border:'1px solid #0d1e3a', cursor:'zoom-in' }} />
      ) : (
        <div style={{ width:'100%', height:150, background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'#3a5a8a' }}>
          {failed ? 'No photo' : 'Loading...'}
        </div>
      )}
    </div>
  );
}

// ================================
// PENDING REQUEST CARD — photo review + editable DOB before approval
// ================================
function PendingRequestCard({ r, onApprove, onDeny }) {
  const [dob, setDob] = useState(r.dob || '');

  return (
    <div style={{ background:'#0a0800', border:'1px solid #3a2800', borderRadius:12, padding:16, marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div>
          <p style={{ fontSize:14, color:'#e8e8e8', marginBottom:2 }}>{r.name}</p>
          <p style={{ fontSize:12, color:'#3a5a8a' }}>{r.phone} • {r.address} • {r.zip}</p>
        </div>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:12 }}>
        <AdminPhoto phone={r.phone} type="idPhoto" label="ID Photo" />
        <AdminPhoto phone={r.phone} type="selfie" label="Selfie" />
      </div>

      <div style={{ display:'flex', alignItems:'flex-end', gap:12, flexWrap:'wrap' }}>
        <div>
          <label style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 }}>
            Date of birth (confirm against the ID)
          </label>
          <input type="date" value={dob} onChange={e=>setDob(e.target.value)}
            style={{ padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, fontFamily:'DM Sans,sans-serif' }} />
          <p style={{ fontSize:10, color:'#3a5a8a', marginTop:4 }}>Applicant entered: {r.dob || '—'}</p>
        </div>
        <div style={{ display:'flex', gap:8, marginLeft:'auto' }}>
          <button onClick={()=>onApprove({ ...r, dob })} style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            Approve + Generate MID
          </button>
          <button onClick={()=>onDeny(r)} style={{ background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            Deny
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================
// DRIVERS TAB - PHASE 2
// ================================
function DriversTab({ drivers, orders, members, staff, cashoutRequests, setCashoutRequests, onSetStatus, onRefresh }) {
  const fldStyle = { width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:12, outline:'none', fontFamily:'DM Sans,sans-serif' };
  const lblStyle = { fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 };

  const [showAdd, setShowAdd]     = useState(false);
  const [newDriver, setNewDriver] = useState({ name:'', phone:'', payType:'flat', rate:'' });
  const [adding, setAdding]       = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const [search, setSearch]       = useState('');
  const [profileTarget, setProfileTarget] = useState(null); // driver whose profile/delivery history is open
  // The Drivers sheet's own "today"/"week" columns never reset — nothing
  // in this codebase has ever zeroed them, so they're really "since the
  // driver was created." A driver's real "today" earnings is summed here
  // from the order-transaction records instead (see utils/orderTransactions.js),
  // refetched whenever a delivery lands (orders changing is that signal).
  const [todaysTxns, setTodaysTxns] = useState([]);
  useEffect(() => {
    axios.get('/api/orders/transactions/today').then(r => setTodaysTxns(r.data.transactions || [])).catch(()=>{});
  }, [orders]);
  const earnedTodayFor = (driverName) => todaysTxns
    .filter(t => t.driver?.name === driverName)
    .reduce((s, t) => s + (t.driver.earned || 0), 0);
  const updNew = (f,v) => setNewDriver(p=>({...p,[f]:v}));

  async function addDriver(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const r = await axios.post('/api/drivers', newDriver);
      alert(`✅ Driver added!\nTell them to go to /driver/activate and sign in as "${r.data.name}" to set their own PIN.`);
      setNewDriver({ name:'', phone:'', payType:'flat', rate:'' });
      setShowAdd(false);
      onRefresh();
    } catch(e) { alert(e.response?.data?.error || 'Failed to add driver'); }
    finally { setAdding(false); }
  }

  async function approveCashout(req) {
    const pwd = window.prompt(`Enter your password to approve ${req.balance} coin payout for ${req.driverName}:`);
    if (!pwd) return;
    try {
      await axios.post('/api/drivers/cashout-approve', { driverName: req.driverName, password: pwd });
      setCashoutRequests(p => p.filter(x => x.requestId !== req.requestId));
      alert(`✅ Payout approved for ${req.driverName}! Balance reset to 0.`);
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  async function rejectCashout(req) {
    const pwd = window.prompt(`Enter your password to deny this payout request from ${req.driverName} (no balance is touched):`);
    if (!pwd) return;
    try {
      await axios.post('/api/drivers/cashout-reject', { requestId: req.requestId, password: pwd });
      setCashoutRequests(p => p.filter(x => x.requestId !== req.requestId));
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
        <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2 }}>Drivers</h2>
        <button onClick={()=>setShowAdd(!showAdd)} style={{ background:'#c0101a', color:'#fff', border:'none', padding:'7px 14px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
          {showAdd ? 'Cancel' : '+ Add Driver'}
        </button>
      </div>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:14 }}>Monitor roster, earnings, and payouts</p>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search drivers — name or phone..."
        style={{ background:'#0a0f20', border:'1px solid #0d1e3a', color:'#e8e8e8', borderRadius:8, padding:'6px 12px', fontSize:12, fontFamily:'DM Sans,sans-serif', width:260, marginBottom:20 }} />

      {showAdd && (
        <div style={{ background:'#06091a', border:'1px solid #1e3a6a', borderRadius:14, padding:20, marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:16, letterSpacing:1, marginBottom:14 }}>Add Driver</p>
          <form onSubmit={addDriver}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={lblStyle}>Name</label><input style={fldStyle} type="text" placeholder="Driver's name" value={newDriver.name} onChange={e=>updNew('name',e.target.value)} required /></div>
              <div><label style={lblStyle}>Phone Number</label><input style={fldStyle} type="tel" placeholder="+1 (555) 000-0000" value={newDriver.phone} onChange={e=>updNew('phone',e.target.value)} required /></div>
              <div>
                <label style={lblStyle}>Pay Type</label>
                <select style={fldStyle} value={newDriver.payType} onChange={e=>updNew('payType',e.target.value)}>
                  <option value="flat">Flat rate per delivery</option>
                  <option value="commission">% commission</option>
                </select>
              </div>
              <div><label style={lblStyle}>{newDriver.payType==='commission' ? 'Commission %' : 'Coins per delivery'}</label><input style={fldStyle} type="number" placeholder="0" value={newDriver.rate} onChange={e=>updNew('rate',e.target.value)} /></div>
            </div>
            <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:12 }}>No PIN is sent — the driver sets their own PIN the first time they sign in with this name.</p>
            <button type="submit" disabled={adding} style={{ background:'#1e3a8a', color:'#fff', border:'none', padding:'9px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>
              {adding ? 'Adding...' : 'Add Driver'}
            </button>
          </form>
        </div>
      )}

      {/* Pending Payouts */}
      {cashoutRequests.length > 0 && (
        <div style={{ background:'#0a1a10', border:'1px solid #0a3020', borderRadius:14, padding:16, marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, color:'#1a9e5a', letterSpacing:1, marginBottom:12 }}>
            💰 Pending Payouts ({cashoutRequests.length})
          </p>
          {cashoutRequests.map(r => {
            // The request's own `balance` is just what it looked like
            // when asked for — approval always pays the driver's real
            // CURRENT balance, never that snapshot. Looking it up live
            // here means admin sees the same "is this still fundable"
            // picture the backend will actually enforce, instead of
            // finding out only after a rejected approve.
            const live = drivers.find(d => String(d.name).toLowerCase().trim() === String(r.driverName).toLowerCase().trim());
            const liveBalance = live ? parseFloat(live.balance) || 0 : null;
            const unfundable = liveBalance !== null && liveBalance <= 0;
            return (
              <div key={r.requestId} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #0a3020' }}>
                <div>
                  <p style={{ fontSize:14, marginBottom:2 }}>
                    {live
                      ? <button onClick={()=>setProfileTarget(live)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{r.driverName}</button>
                      : <span style={{ color:'#e8e8e8' }}>{r.driverName}</span>}
                  </p>
                  <p style={{ fontSize:12, color:'#3a5a8a' }}>
                    {liveBalance !== null && liveBalance !== r.balance ? `${liveBalance} coins now (requested at ${r.balance})` : `${r.balance} coins`} • {r.requestedAt}
                  </p>
                  {unfundable && (
                    <p style={{ fontSize:11, color:'#c0101a', marginTop:3 }}>⚠ Insufficient funds — already paid out or balance since cleared. Deny to clear this request.</p>
                  )}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => rejectCashout(r)} style={{ background:'#1a0a0a', color:'#c0101a', border:'1px solid #301010', borderRadius:8, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    Deny
                  </button>
                  <button onClick={() => approveCashout(r)} disabled={unfundable}
                    style={{ background: unfundable ? '#0a1a10' : '#0a2010', color: unfundable ? '#3a5a3a' : '#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'7px 16px', fontSize:12, cursor: unfundable ? 'not-allowed' : 'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                    Approve Payout
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Driver Cards */}
      {(() => {
        const q = search.trim().toLowerCase();
        const visibleDrivers = q ? drivers.filter(d => [d.name, d.phone].some(f => String(f||'').toLowerCase().includes(q))) : drivers;
        if (q && visibleDrivers.length === 0) return <p style={{ fontSize:12, color:'#3a5a8a' }}>No drivers match "{search}".</p>;
        return (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
        {visibleDrivers.map(d => {
          const active    = orders.filter(o => o.assigned===d.name && o.status!=='Delivered' && o.status!=='Attempted Delivery' && o.status!=='Cancelled').length;
          // The card says "today" — make it actually mean that (see wasClosedToday).
          const delivered = orders.filter(o => o.assigned===d.name && o.status==='Delivered' && wasClosedToday(o)).length;
          const attempted = orders.filter(o => o.assigned===d.name && o.status==='Attempted Delivery' && wasClosedToday(o)).length;
          return (
            <div key={d.id||d.name} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
              {/* Avatar */}
              <div style={{ width:40, height:40, borderRadius:'50%', background:'#0a1530', border:'2px solid #1e3a6a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Bebas Neue,sans-serif', fontSize:18, color:'#1e6bff', marginBottom:10 }}>
                {d.name?.[0] || '?'}
              </div>
              <button onClick={()=>setProfileTarget(d)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', display:'block', fontSize:14, fontWeight:500, color:'#e8e8e8', marginBottom:2, textAlign:'left', fontFamily:'DM Sans,sans-serif', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{d.name}</button>
              {d.phone && <a href={`tel:${d.phone}`} style={{ fontSize:11, color:'#1e6bff', textDecoration:'none', display:'block', marginBottom:4 }}>📞 {d.phone}</a>}
              <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:6 }}>
                {delivered} delivered • {attempted} attempted today
              </p>
              <p style={{ fontSize:11, color: active > 0 ? '#f0a020' : '#1a9e5a', marginBottom:10 }}>
                ● {active > 0 ? `${active} active` : 'Available'}
              </p>

              {/* Earnings */}
              <div style={{ background:'#03050f', borderRadius:8, padding:'8px 10px', marginBottom:10 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:11, color:'#3a5a8a' }}>Balance</span>
                  <span style={{ fontSize:12, fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a', letterSpacing:1 }}>{d.balance || 0}c</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:11, color:'#3a5a8a' }}>Today</span>
                  <span style={{ fontSize:12, color:'#e8e8e8' }}>{earnedTodayFor(d.name)}c</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:11, color:'#3a5a8a' }}>This Week</span>
                  <span style={{ fontSize:12, color:'#e8e8e8' }}>{d.week || 0}c</span>
                </div>
              </div>

              {/* Pay type badge */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontSize:10, background: d.payType==='commission' ? '#0a1530' : '#0a2010', color: d.payType==='commission' ? '#1e6bff' : '#1a9e5a', border:`1px solid ${d.payType==='commission' ? '#1e3a6a' : '#0a3020'}`, borderRadius:20, padding:'2px 8px' }}>
                  {d.payType==='commission' ? `${d.rate}% commission` : `${d.rate}c flat`}
                </span>
                <span style={{ fontSize:10, color: d.status === 'suspended' ? '#c0101a' : '#1a9e5a' }}>
                  ● {d.status === 'suspended' ? 'Suspended' : 'Active'}
                </span>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={()=>setChatTarget({ role:'driver', id:d.name, label:d.name })} style={{ flex:1, background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>💬 Message</button>
                {d.status === 'suspended' ? (
                  <button onClick={()=>onSetStatus(d.name,'active')} style={{ flex:1, background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Reactivate</button>
                ) : (
                  <button onClick={()=>onSetStatus(d.name,'suspended')} style={{ flex:1, background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Suspend</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
        );
      })()}

      {chatTarget && (
        <ChatThread
          title={`Message ${chatTarget.label}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          postUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          onClose={() => setChatTarget(null)}
        />
      )}

      {profileTarget && (
        <DriverProfileModal driver={profileTarget} orders={orders} members={members} staff={staff} onClose={()=>setProfileTarget(null)} />
      )}

      {/* Drivers Sheet note */}
      <div style={{ marginTop:20, background:'#06091a', border:'1px solid #0a1628', borderRadius:12, padding:14 }}>
        <p style={{ fontSize:12, color:'#3a5a8a', lineHeight:1.6 }}>
          💡 Pay type and rate are set when a driver is added. To change them for an existing driver, edit your <strong style={{ color:'#e8e8e8' }}>Drivers</strong> sheet directly:
          Column I = PayType (<code style={{ color:'#1e6bff' }}>flat</code> or <code style={{ color:'#1e6bff' }}>commission</code>) •
          Column J = Rate (coins per delivery OR % commission)
        </p>
      </div>
    </div>
  );
}

// ================================
// DRIVER PROFILE — account details plus every delivery this driver has
// ever been assigned (any status), the same pattern as MemberProfileModal.
// orders is already the full /api/orders/all history the dashboard holds
// for Dispatch, just filtered here by assigned driver — no extra API call.
// ================================
function DriverProfileModal({ driver, orders, members, staff, onClose }) {
  const [detailOrder, setDetailOrder] = useState(null);
  const [memberProfile, setMemberProfile] = useState(null); // customer whose own profile is open from here
  const [staffProfile, setStaffProfile] = useState(null); // staffer opened from a customer's "signed up by" link
  const history = orders.filter(o => String(o.assigned).toLowerCase().trim() === String(driver.name).toLowerCase().trim());
  const statusColor = (s) => s === 'Delivered' ? '#1a9e5a' : s === 'Attempted Delivery' ? '#3a5a8a' : '#f0a020';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:620, maxHeight:'85vh', background:'#06091a', border:'1px solid #1e3a6a', borderRadius:16, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #0a1628' }}>
          <div>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:1, color:'#e8e8e8' }}>{driver.name}</p>
            <p style={{ fontSize:11, color:'#3a5a8a' }}>{driver.phone || '—'} • {driver.status === 'suspended' ? 'Suspended' : 'Active'}</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ padding:'16px 20px', borderBottom:'1px solid #0a1628', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, fontSize:12 }}>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Pay type</p><p style={{ color:'#e8e8e8' }}>{driver.payType === 'commission' ? `${driver.rate}% commission` : `${driver.rate} coins flat`}</p></div>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Balance</p><p style={{ fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a', fontSize:15 }}>{driver.balance || 0} coins</p></div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px' }}>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Delivery History — {history.length} order{history.length === 1 ? '' : 's'}</p>
          {history.length === 0 ? (
            <p style={{ fontSize:12, color:'#3a5a8a' }}>No deliveries yet.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>
                {['OID','Member','Summary','Total','Status','Placed'].map(h => (
                  <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'6px 8px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {history.map(o => (
                  <tr key={o.oid}>
                    <td style={{ padding:'8px' }}>
                      <button onClick={()=>setDetailOrder(o)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', fontFamily:'Bebas Neue,sans-serif', color:'#1e6bff', letterSpacing:1, fontSize:12, textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.oid}</button>
                    </td>
                    <td style={{ padding:'8px', color:'#e8e8e8' }}>
                      {(() => {
                        const m = members?.find(mm => String(mm.mid) === String(o.mid));
                        return m
                          ? <button onClick={()=>setMemberProfile(m)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{o.name}</button>
                          : o.name;
                      })()}
                    </td>
                    <td style={{ padding:'8px', color:'#888', maxWidth:140 }}>{o.summary}</td>
                    <td style={{ padding:'8px', fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a' }}>{o.total}</td>
                    <td style={{ padding:'8px', color:statusColor(o.status) }}>{o.status || 'Pending'}</td>
                    <td style={{ padding:'8px', color:'#3a5a8a' }}>{o.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detailOrder && <OrderDetailModal order={detailOrder} orders={orders} members={members} staff={staff} onClose={()=>setDetailOrder(null)} />}
      {memberProfile && (
        <MemberProfileModal
          member={memberProfile}
          orders={orders}
          members={members}
          staff={staff}
          onSelectMember={setMemberProfile}
          onSelectStaff={(username) => setStaffProfile(staff?.find(s => s.username === username))}
          onClose={() => setMemberProfile(null)}
        />
      )}
      {staffProfile && <StaffProfileModal staffMember={staffProfile} members={members} orders={orders} staff={staff} onClose={()=>setStaffProfile(null)} />}
    </div>
  );
}

// ================================
// STAFF TAB (promo team) — roster, add, payouts, and a quick look at
// who's bringing in the most signups.
// ================================
function StaffTab({ staff, members, orders, drivers, cashoutRequests, setCashoutRequests, onSetStatus, onRefresh }) {
  const fldStyle = { width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:12, outline:'none', fontFamily:'DM Sans,sans-serif' };
  const lblStyle = { fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:5 };

  const [showAdd, setShowAdd]   = useState(false);
  const [newStaff, setNewStaff] = useState({ name:'', username:'', phone:'', leadRate:'', conversionRate:'' });
  const [adding, setAdding]     = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const [showLog, setShowLog]   = useState(false);
  const [log, setLog]           = useState([]);
  const [editingRates, setEditingRates] = useState(null); // username currently being edited
  const [rateEdit, setRateEdit] = useState({ leadRate:'', conversionRate:'' });
  const [savingRates, setSavingRates] = useState(false);
  const [search, setSearch]     = useState('');
  const [profileTarget, setProfileTarget] = useState(null); // staffer whose profile/signups is open
  const updNew = (f,v) => setNewStaff(p=>({...p,[f]:v}));

  async function addStaff(e) {
    e.preventDefault();
    setAdding(true);
    try {
      const r = await axios.post('/api/staff', newStaff);
      alert(`✅ Staff added!\nGive them the username "${r.data.username}" — they'll activate at /staff/activate using that username plus the phone number on file, then choose their own PIN.`);
      setNewStaff({ name:'', username:'', phone:'', leadRate:'', conversionRate:'' });
      setShowAdd(false);
      onRefresh();
    } catch(e) { alert(e.response?.data?.error || 'Failed to add staff member'); }
    finally { setAdding(false); }
  }

  function startEditRates(s) {
    setEditingRates(s.username);
    setRateEdit({ leadRate: String(s.leadRate || 0), conversionRate: String(s.conversionRate || 0) });
  }

  async function saveRates(username) {
    setSavingRates(true);
    try {
      await axios.put(`/api/staff/${encodeURIComponent(username)}/rates`, rateEdit);
      setEditingRates(null);
      onRefresh();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update rates'); }
    finally { setSavingRates(false); }
  }

  async function approveCashout(req) {
    const pwd = window.prompt(`Enter your password to approve ${req.balance} coin payout for ${req.staffName}:`);
    if (!pwd) return;
    try {
      await axios.post('/api/staff/cashout-approve', { staffUsername: req.staffUsername, password: pwd });
      setCashoutRequests(p => p.filter(x => x.requestId !== req.requestId));
      alert(`✅ Payout approved for ${req.staffName}! Balance reset to 0.`);
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  async function rejectCashout(req) {
    const pwd = window.prompt(`Enter your password to deny this payout request from ${req.staffName} (no balance is touched):`);
    if (!pwd) return;
    try {
      await axios.post('/api/staff/cashout-reject', { requestId: req.requestId, password: pwd });
      setCashoutRequests(p => p.filter(x => x.requestId !== req.requestId));
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  async function loadLog() {
    if (!showLog) {
      try { const r = await axios.get('/api/members/verification-log'); setLog(r.data.log); } catch(e) {}
    }
    setShowLog(s => !s);
  }

  const signupCount = (username) => members.filter(m => m.signedUpByStaff === username).length;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
        <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2 }}>Staff</h2>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={loadLog} style={{ background:'transparent', color:'#1e6bff', border:'1px solid #1e3a6a', padding:'7px 14px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            {showLog ? 'Hide' : 'View'} Verification Log
          </button>
          <button onClick={()=>setShowAdd(!showAdd)} style={{ background:'#c0101a', color:'#fff', border:'none', padding:'7px 14px', borderRadius:9, fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
            {showAdd ? 'Cancel' : '+ Add Staff'}
          </button>
        </div>
      </div>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:14 }}>Your promo team — signups, payouts, and who's bringing people in</p>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search staff — name, username, phone..."
        style={{ background:'#0a0f20', border:'1px solid #0d1e3a', color:'#e8e8e8', borderRadius:8, padding:'6px 12px', fontSize:12, fontFamily:'DM Sans,sans-serif', width:260, marginBottom:20 }} />

      {showAdd && (
        <div style={{ background:'#06091a', border:'1px solid #1e3a6a', borderRadius:14, padding:20, marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:16, letterSpacing:1, marginBottom:14 }}>Add Staff Member</p>
          <form onSubmit={addStaff}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={lblStyle}>Name</label><input style={fldStyle} type="text" placeholder="Their name" value={newStaff.name} onChange={e=>updNew('name',e.target.value)} required /></div>
              <div><label style={lblStyle}>Username</label><input style={fldStyle} type="text" placeholder="What you'll hand them to activate" value={newStaff.username} onChange={e=>updNew('username',e.target.value)} required /></div>
              <div><label style={lblStyle}>Phone Number</label><input style={fldStyle} type="tel" placeholder="+1 (555) 000-0000" value={newStaff.phone} onChange={e=>updNew('phone',e.target.value)} required /></div>
              <div />
              <div><label style={lblStyle}>Coins per Lead (on signup)</label><input style={fldStyle} type="number" placeholder="0" value={newStaff.leadRate} onChange={e=>updNew('leadRate',e.target.value)} /></div>
              <div><label style={lblStyle}>Coins per Conversion (first purchase)</label><input style={fldStyle} type="number" placeholder="0" value={newStaff.conversionRate} onChange={e=>updNew('conversionRate',e.target.value)} /></div>
            </div>
            <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:12 }}>Lead coins land the moment they add a member; conversion coins only land if that member actually completes a first delivery — a lead who never buys never costs more than the lead rate. No PIN is sent — at /staff/activate they'll enter this username plus their phone number on file (a quick two-factor check) before choosing their own PIN.</p>
            <button type="submit" disabled={adding} style={{ background:'#1e3a8a', color:'#fff', border:'none', padding:'9px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'DM Sans,sans-serif' }}>
              {adding ? 'Adding...' : 'Add Staff Member'}
            </button>
          </form>
        </div>
      )}

      {showLog && (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:20, maxHeight:300, overflowY:'auto' }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:16, letterSpacing:1, marginBottom:10 }}>Verification Log</p>
          {log.length === 0 && <p style={{ fontSize:12, color:'#3a5a8a' }}>No entries yet.</p>}
          {log.map((l, i) => (
            <div key={i} style={{ padding:'8px 0', borderTop: i>0 ? '1px solid #0a1628' : 'none', fontSize:12 }}>
              <span style={{ color:'#e8e8e8' }}>{l.name}</span>
              <span style={{ color:'#3a5a8a' }}> ({l.mid}) — DOB {l.dob} — verified via </span>
              <span style={{ color: l.method==='barcode' ? '#1a9e5a' : '#f0a020' }}>{l.method}</span>
              <span style={{ color:'#3a5a8a' }}> by {l.verifiedBy} ({l.verifiedByRole}) — {l.timestamp}</span>
            </div>
          ))}
        </div>
      )}

      {/* Pending Payouts */}
      {cashoutRequests.length > 0 && (
        <div style={{ background:'#0a1a10', border:'1px solid #0a3020', borderRadius:14, padding:16, marginBottom:20 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, color:'#1a9e5a', letterSpacing:1, marginBottom:12 }}>
            💰 Pending Payouts ({cashoutRequests.length})
          </p>
          {cashoutRequests.map(r => {
            // The request's own `balance` is just what it looked like
            // when asked for — approval always pays the staffer's real
            // CURRENT balance, never that snapshot. Looking it up live
            // here means admin sees the same "is this still fundable"
            // picture the backend will actually enforce, instead of
            // finding out only after a rejected approve.
            const live = staff.find(s => String(s.username).toLowerCase().trim() === String(r.staffUsername).toLowerCase().trim());
            const liveBalance = live ? parseFloat(live.balance) || 0 : null;
            const unfundable = liveBalance !== null && liveBalance <= 0;
            return (
            <div key={r.requestId} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #0a3020' }}>
              <div>
                <p style={{ fontSize:14, marginBottom:2 }}>
                  {live
                    ? <button onClick={()=>setProfileTarget(live)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{r.staffName}</button>
                    : <span style={{ color:'#e8e8e8' }}>{r.staffName}</span>}
                </p>
                <p style={{ fontSize:12, color:'#3a5a8a' }}>
                  {liveBalance !== null && liveBalance !== r.balance ? `${liveBalance} coins now (requested at ${r.balance})` : `${r.balance} coins`} • {r.requestedAt}
                </p>
                {unfundable && (
                  <p style={{ fontSize:11, color:'#c0101a', marginTop:3 }}>⚠ Insufficient funds — already paid out or balance since cleared. Deny to clear this request.</p>
                )}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => rejectCashout(r)} style={{ background:'#1a0a0a', color:'#c0101a', border:'1px solid #301010', borderRadius:8, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  Deny
                </button>
                <button onClick={() => approveCashout(r)} disabled={unfundable}
                  style={{ background: unfundable ? '#0a1a10' : '#0a2010', color: unfundable ? '#3a5a3a' : '#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'7px 16px', fontSize:12, cursor: unfundable ? 'not-allowed' : 'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                  Approve Payout
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Staff promos moved into the main Promos tab — it now has an
          audience selector (members/drivers/staff) and an optional
          trackable goal, so there's one place to post any promo instead
          of a separate panel just for staff. */}
      <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:14, marginBottom:20 }}>
        <p style={{ fontSize:12, color:'#3a5a8a' }}>
          📢 Looking for staff promos? Post and manage those from the <strong style={{ color:'#e8e8e8' }}>Promos</strong> tab now — pick "Staff" as the audience when creating one.
        </p>
      </div>

      {/* Staff Cards */}
      {(() => {
        const q = search.trim().toLowerCase();
        const visibleStaff = q ? staff.filter(s => [s.name, s.username, s.phone].some(f => String(f||'').toLowerCase().includes(q))) : staff;
        if (q && visibleStaff.length === 0) return <p style={{ fontSize:12, color:'#3a5a8a' }}>No staff match "{search}".</p>;
        return (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
        {visibleStaff.map(s => (
          <div key={s.id||s.name} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'#0a1530', border:'2px solid #1e3a6a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Bebas Neue,sans-serif', fontSize:18, color:'#1e6bff', marginBottom:10 }}>
              {s.name?.[0] || '?'}
            </div>
            <button onClick={()=>setProfileTarget(s)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', display:'block', fontSize:14, fontWeight:500, color:'#e8e8e8', marginBottom:2, textAlign:'left', fontFamily:'DM Sans,sans-serif', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{s.name}</button>
            <p style={{ fontSize:10, color:'#1e3a6a', marginBottom:4 }}>@{s.username}</p>
            {s.phone && <a href={`tel:${s.phone}`} style={{ fontSize:11, color:'#1e6bff', textDecoration:'none', display:'block', marginBottom:4 }}>📞 {s.phone}</a>}
            <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>{signupCount(s.username)} members signed up</p>

            <div style={{ background:'#03050f', borderRadius:8, padding:'8px 10px', marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:11, color:'#3a5a8a' }}>Balance</span>
                <span style={{ fontSize:12, fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a', letterSpacing:1 }}>{s.balance || 0}c</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:11, color:'#3a5a8a' }}>Today</span>
                <span style={{ fontSize:12, color:'#e8e8e8' }}>{s.today || 0}c</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:11, color:'#3a5a8a' }}>This Week</span>
                <span style={{ fontSize:12, color:'#e8e8e8' }}>{s.week || 0}c</span>
              </div>
            </div>

            {editingRates === s.username ? (
              <div style={{ background:'#03050f', borderRadius:8, padding:10, marginBottom:10 }}>
                <label style={lblStyle}>Lead rate</label>
                <input type="number" style={{ ...fldStyle, marginBottom:8 }} value={rateEdit.leadRate} onChange={e=>setRateEdit(p=>({...p, leadRate:e.target.value}))} />
                <label style={lblStyle}>Conversion rate</label>
                <input type="number" style={{ ...fldStyle, marginBottom:8 }} value={rateEdit.conversionRate} onChange={e=>setRateEdit(p=>({...p, conversionRate:e.target.value}))} />
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={()=>saveRates(s.username)} disabled={savingRates} style={{ flex:1, background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:7, padding:'6px 0', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    {savingRates ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={()=>setEditingRates(null)} style={{ flex:1, background:'none', color:'#3a5a8a', border:'1px solid #0d1e3a', borderRadius:7, padding:'6px 0', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div onClick={()=>startEditRates(s)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, cursor:'pointer' }} title="Click to edit rates">
                <span style={{ fontSize:10, background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:20, padding:'2px 8px' }}>
                  {s.leadRate || 0}c lead / {s.conversionRate || 0}c sale ✎
                </span>
                <span style={{ fontSize:10, color: s.status === 'suspended' ? '#c0101a' : '#1a9e5a' }}>
                  ● {s.status === 'suspended' ? 'Suspended' : 'Active'}
                </span>
              </div>
            )}
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={()=>setChatTarget({ role:'staff', id:s.username, label:s.name })} style={{ flex:1, background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>💬 Message</button>
              {s.status === 'suspended' ? (
                <button onClick={()=>onSetStatus(s.username,'active')} style={{ flex:1, background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Reactivate</button>
              ) : (
                <button onClick={()=>onSetStatus(s.username,'suspended')} style={{ flex:1, background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:8, padding:'7px 0', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Suspend</button>
              )}
            </div>
          </div>
        ))}
      </div>
        );
      })()}

      {chatTarget && (
        <ChatThread
          title={`Message ${chatTarget.label}`}
          selfRole="admin"
          fetchUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          postUrl={`/api/messages/thread/${chatTarget.role}/${encodeURIComponent(chatTarget.id)}`}
          onClose={() => setChatTarget(null)}
        />
      )}

      {profileTarget && (
        <StaffProfileModal staffMember={profileTarget} members={members} orders={orders} drivers={drivers} staff={staff} onClose={()=>setProfileTarget(null)} />
      )}
    </div>
  );
}

// ================================
// STAFF PROFILE — account details plus every member this staffer has
// signed up (their own "history"), same pattern as the other profile
// modals. members is already loaded for the whole dashboard, filtered
// here by signedUpByStaff — no extra API call needed.
// ================================
function StaffProfileModal({ staffMember, members, orders, drivers, staff, onClose }) {
  const signups = members.filter(m => m.signedUpByStaff === staffMember.username);
  const [memberProfile, setMemberProfile] = useState(null); // signed-up member whose own profile is open from here
  const [nestedStaffProfile, setNestedStaffProfile] = useState(null); // a DIFFERENT staffer, reached via a member's own "signed up by" link

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:560, maxHeight:'85vh', background:'#06091a', border:'1px solid #1e3a6a', borderRadius:16, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #0a1628' }}>
          <div>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, letterSpacing:1, color:'#e8e8e8' }}>{staffMember.name}</p>
            <p style={{ fontSize:11, color:'#3a5a8a' }}>@{staffMember.username} • {staffMember.phone || '—'} • {staffMember.status === 'suspended' ? 'Suspended' : 'Active'}</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ padding:'16px 20px', borderBottom:'1px solid #0a1628', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, fontSize:12 }}>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Lead / Conversion rate</p><p style={{ color:'#e8e8e8' }}>{staffMember.leadRate || 0}c lead / {staffMember.conversionRate || 0}c sale</p></div>
          <div><p style={{ color:'#3a5a8a', marginBottom:2 }}>Balance</p><p style={{ fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a', fontSize:15 }}>{staffMember.balance || 0} coins</p></div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'14px 20px' }}>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Members Signed Up — {signups.length}</p>
          {signups.length === 0 ? (
            <p style={{ fontSize:12, color:'#3a5a8a' }}>No signups yet.</p>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead><tr>
                {['M.I.D.','Name','Phone','Wallet','Status'].map(h => (
                  <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'6px 8px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {signups.map(m => (
                  <tr key={m.mid}>
                    <td style={{ padding:'8px', fontFamily:'Bebas Neue,sans-serif', color:'#1e6bff', letterSpacing:1 }}>{m.mid}</td>
                    <td style={{ padding:'8px' }}>
                      <button onClick={()=>setMemberProfile(m)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{m.name}</button>
                    </td>
                    <td style={{ padding:'8px', color:'#888' }}>{m.phone}</td>
                    <td style={{ padding:'8px', fontFamily:'Bebas Neue,sans-serif', color:'#1a9e5a' }}>{parseFloat(m.wallet||0).toFixed(0)}</td>
                    <td style={{ padding:'8px', color: m.status === 'suspended' ? '#c0101a' : '#1a9e5a' }}>{m.status === 'suspended' ? 'Suspended' : 'Active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {memberProfile && (
        <MemberProfileModal
          member={memberProfile}
          orders={orders}
          members={members}
          staff={staff}
          onSelectMember={setMemberProfile}
          onSelectStaff={(username) => setNestedStaffProfile(staff?.find(s => s.username === username))}
          onClose={() => setMemberProfile(null)}
        />
      )}
      {nestedStaffProfile && <StaffProfileModal staffMember={nestedStaffProfile} members={members} orders={orders} drivers={drivers} staff={staff} onClose={()=>setNestedStaffProfile(null)} />}
    </div>
  );
}

// ================================
// LIVE MAP - driver positions, updated as pings arrive
// ================================
function LiveMapTab({ driverLocations }) {
  const hasDrivers = driverLocations.length > 0;
  const center = hasDrivers
    ? [
        driverLocations.reduce((s, d) => s + d.lat, 0) / driverLocations.length,
        driverLocations.reduce((s, d) => s + d.lng, 0) / driverLocations.length
      ]
    : [39.8283, -98.5795]; // geographic center of the US — fallback when nobody's sharing location yet

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Live Map</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>
        {hasDrivers ? `${driverLocations.length} driver${driverLocations.length===1?'':'s'} currently sharing location` : 'No drivers are sharing location right now'}
      </p>

      {!hasDrivers && (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:24, textAlign:'center', marginBottom:16 }}>
          <p style={{ fontSize:13, color:'#3a5a8a' }}>Location sharing starts automatically once a driver has an active delivery — check back once one's out on a run.</p>
        </div>
      )}

      <div style={{ borderRadius:14, overflow:'hidden', border:'1px solid #0a1628' }}>
        <MapContainer key={hasDrivers ? 'has-drivers' : 'empty'} center={center} zoom={hasDrivers ? 13 : 4} style={{ height: 520, width: '100%' }}>
          {/* Esri World Street Map — free, no API key, no account, no Railway
              env var needed. Raw OpenStreetMap tiles (tried here briefly)
              are NOT an option: OSM's volunteer-run tile.openstreetmap.org
              already blocked this exact app once before under real
              production traffic (see commit 30ed3dd, Oct 16) — that's why
              this used flat Esri gray before CARTO Voyager replaced it, and
              why CARTO's now-broken free tier can't be swapped back to raw
              OSM either. Esri's own basemap tiles are built for exactly
              this kind of third-party embedding (no blocking, no key), and
              World_Street_Map is full color with real street/place labels
              instead of the flat gray this app used to have. */}
          <TileLayer
            attribution='Tiles &copy; <a href="https://www.esri.com">Esri</a> &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
          {driverLocations.map(d => (
            <React.Fragment key={d.driverName}>
              <Marker position={[d.lat, d.lng]} icon={driverPin}>
                <Popup>
                  <strong>{d.driverName}</strong><br />
                  Updated {timeAgo(d.updatedAt)}
                  {d.destination && <><br />En route to order {d.destination.oid}</>}
                </Popup>
              </Marker>
              {d.destination && (
                <>
                  <Marker position={[d.destination.lat, d.destination.lng]} icon={destinationPin}>
                    <Popup>
                      <strong>Delivery stop</strong><br />
                      {d.destination.address}<br />
                      Order {d.destination.oid}
                    </Popup>
                  </Marker>
                  <Polyline positions={[[d.lat, d.lng], [d.destination.lat, d.destination.lng]]} pathOptions={{ color: '#1e6bff', weight: 2, dashArray: '6 6' }} />
                </>
              )}
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

// ================================
// COINS TAB - Reload purchase approvals (Cash App, Venmo, Chime, Apple Pay)
// ================================
const PAYMENT_METHOD_LABELS = { cashapp: 'Cash App', venmo: 'Venmo', chime: 'Chime', applepay: 'Apple Pay' };
function formatPaidTo(r) {
  if (!r.cashtag) return '';
  const method = r.method || 'cashapp';
  if (method === 'venmo') return `@${r.cashtag}`;
  if (method === 'applepay') return r.cashtag; // already a phone number
  return `$${r.cashtag}`; // cashapp, chime
}

function CoinsTab({ coinRequests, setCoinRequests }) {
  async function approveRequest(r) {
    const paidTo = formatPaidTo(r);
    const pwd = window.prompt(`Enter your password to confirm you received $${r.total} from ${r.name} via ${PAYMENT_METHOD_LABELS[r.method || 'cashapp']}${paidTo ? ` (${paidTo})` : ''} and credit ${r.coinAmount} coins:`);
    if (!pwd) return;
    try {
      await axios.post('/api/coins/purchase-approve', { requestId: r.requestId, password: pwd });
      setCoinRequests(p => p.filter(x => x.requestId !== r.requestId));
      alert(`✅ ${r.coinAmount} coins credited to ${r.name}`);
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  async function rejectRequest(r) {
    const pwd = window.prompt(`Enter your password to reject this request from ${r.name} (payment not received):`);
    if (!pwd) return;
    try {
      await axios.post('/api/coins/purchase-reject', { requestId: r.requestId, password: pwd });
      setCoinRequests(p => p.filter(x => x.requestId !== r.requestId));
    } catch(e) { alert(e.response?.data?.error || 'Failed — check password'); }
  }

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Coin Purchases</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Confirm Cash App, Venmo, Chime, or Apple Pay payments before crediting coins</p>

      {coinRequests.length === 0 ? (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:24, textAlign:'center' }}>
          <p style={{ fontSize:13, color:'#3a5a8a' }}>No pending coin purchase requests</p>
        </div>
      ) : (
        <div style={{ background:'#0a1a10', border:'1px solid #0a3020', borderRadius:14, padding:16 }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, color:'#1a9e5a', letterSpacing:1, marginBottom:12 }}>
            🪙 Pending Requests ({coinRequests.length})
          </p>
          {coinRequests.map(r => (
            <div key={r.requestId} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #0a3020' }}>
              <div>
                <p style={{ fontSize:14, color:'#e8e8e8', marginBottom:2 }}>{r.name} <span style={{ color:'#3a5a8a' }}>(MID {r.mid})</span></p>
                <p style={{ fontSize:12, color:'#3a5a8a' }}>
                  {r.coinAmount} coins for ${r.total} • {r.requestedAt}
                  {r.cashtag && <> • {PAYMENT_METHOD_LABELS[r.method || 'cashapp']}: <span style={{ color:'#1e6bff' }}>{formatPaidTo(r)}</span></>}
                </p>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => rejectRequest(r)} style={{ background:'#1a0a0a', color:'#c0101a', border:'1px solid #301010', borderRadius:8, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  Reject
                </button>
                <button onClick={() => approveRequest(r)} style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'7px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
                  Confirm Payment & Credit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================
// REPORTS TAB
// ================================
function ReportsTab({ orders, members, drivers, staff, totalCoins, onTopUp }) {
  const totalCirculation = members.reduce((s,m) => s + parseFloat(m.wallet||0), 0);
  const totalSpent       = orders.filter(o=>o.status==='Delivered').reduce((s,o) => s + parseFloat(String(o.total).replace(/[^0-9.]/g,''))||0, 0);
  const totalOrders      = orders.length;
  const avgOrder         = totalOrders > 0 ? totalSpent / orders.filter(o=>o.status==='Delivered').length : 0;
  // Earned the moment a delivery completes, but not logged as a ledger
  // cost until the driver/staffer actually cashes out — so it has to be
  // reserved here too, the same way unspent member wallets are, or
  // "Profit" would only be accurate right after a payout run.
  const owedToTeam = (drivers||[]).reduce((s,d) => s + (parseFloat(d.balance)||0), 0)
                    + (staff||[]).reduce((s,st) => s + (parseFloat(st.balance)||0), 0);

  // Editable elsewhere now (Settings tab) — read-only here, just for the
  // P&L math below (memberOwed, owedToTeam valued in dollars).
  const [rate, setRate] = useState(null);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [period, setPeriod] = useState('all'); // 'all' | 'today'
  const [memberProfile, setMemberProfile] = useState(null); // member whose profile/order history is open
  const [staffProfile, setStaffProfile] = useState(null); // staffer opened from a member's "signed up by" link
  const socket = useSocket();

  useEffect(() => {
    axios.get('/api/coins/rate').then(r => setRate(r.data.rate)).catch(()=>{});
  }, []);

  useEffect(() => {
    setLedgerLoading(true);
    axios.get('/api/ledger/summary', { params: { period } }).then(r => setLedgerSummary(r.data)).catch(()=>{}).finally(()=>setLedgerLoading(false));
  }, [period]);

  // A wallet top-up, Cash App purchase, delivery, cashout, or points
  // redemption anywhere in the system writes a ledger entry — refetch so
  // this tab doesn't sit showing stale numbers until it's switched away
  // from and back, which is what made it look out of sync with Dispatch.
  useEffect(() => {
    if (!socket) return;
    const refresh = () => axios.get('/api/ledger/summary', { params: { period } }).then(r => setLedgerSummary(r.data)).catch(()=>{});
    socket.on('ledger:updated', refresh);
    return () => socket.off('ledger:updated', refresh);
  }, [socket, period]);

  const LEDGER_TYPE_LABELS = {
    wallet_topup: 'Wallet top-ups', cashapp_purchase: 'Cash App purchases',
    driver_payout: 'Driver payouts', staff_payout: 'Staff payouts',
    referral_bonus: 'Referral bonuses', points_redemption: 'Points redemptions'
  };

  const exportData = async (type) => {
    try {
      const r = await axios.get(`/api/sheets/export/${type}`);
      const b = new Blob([JSON.stringify(r.data.data,null,2)], { type:'application/json' });
      const u = URL.createObjectURL(b);
      const a = document.createElement('a'); a.href=u; a.download=`${type}.json`; a.click();
    } catch(e) { alert('Export failed'); }
  };

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Reports</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>Coin economy overview and data export</p>

      {/* Profit & Loss — real dollars, not coin volume. Revenue/Costs come
          from the Ledger sheet (every wallet top-up, approved Cash App
          purchase, and driver/staff CASHOUT); Profit below additionally
          reserves coins that are earned-but-not-yet-settled on both sides
          — unspent member wallets, and driver/staff balances not yet
          cashed out — so it's accurate at any moment, not just right
          after a payout run. */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1 }}>Profit &amp; Loss (real $)</p>
        <div style={{ display:'flex', gap:6 }}>
          {['all', 'today'].map(p => (
            <button key={p} onClick={()=>setPeriod(p)}
              style={{
                background: period === p ? '#0a1530' : 'transparent',
                color: period === p ? '#1e6bff' : '#3a5a8a',
                border: `1px solid ${period === p ? '#1e3a6a' : '#0a1628'}`,
                borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer',
                fontFamily:'DM Sans,sans-serif', fontWeight: period === p ? 500 : 400
              }}>
              {p === 'all' ? 'All Time' : 'Today'}
            </button>
          ))}
        </div>
      </div>
      {ledgerLoading ? (
        <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:24 }}>Loading...</p>
      ) : !ledgerSummary || ledgerSummary.entryCount === 0 ? (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
          <p style={{ fontSize:12, color:'#3a5a8a' }}>
            {period === 'today'
              ? 'No ledger activity yet today — this fills in as wallet top-ups, Cash App purchases, and driver/staff cashouts happen.'
              : 'No ledger entries yet — this fills in as wallet top-ups, Cash App purchases, and driver/staff cashouts happen from here on. Anything from before this feature existed isn\'t retroactively counted.'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:12 }}>
            {[
              { label:'Revenue',        val:ledgerSummary.revenue, color:'#1a9e5a' },
              { label:'Costs (paid out)', val:ledgerSummary.costs, color:'#c0101a' },
              { label:'Net Cash Flow',  val:ledgerSummary.profit,  color:ledgerSummary.profit >= 0 ? '#1a9e5a' : '#c0101a' },
            ].map(m=>(
              <div key={m.label} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
                <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{m.label}</p>
                <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, color:m.color, letterSpacing:1 }}>${m.val.toFixed(2)}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:16 }}>
            {period === 'today' ? (
              <>Net Cash Flow is what's actually moved in/out of the business bank today — it counts today's wallet funding as revenue even if those coins haven't been spent yet. Profit, below, corrects for that, but it's always measured against <strong>current</strong> standing balances owed (not just what changed today) — so it won't net to a clean "today's profit" figure.</>
            ) : (
              <>Net Cash Flow is what's actually moved in/out of the business bank so far — it's the current holdings of the coin ecosystem, not your bottom line. It still counts unspent member balances as revenue, and hasn't yet paid out driver/staff earnings that are sitting uncashed. Profit, below, corrects for both.</>
            )}
          </p>

          {(() => {
            const memberOwed = totalCirculation * (rate || 0);
            const profit = ledgerSummary.profit - memberOwed - owedToTeam;
            return (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:12 }}>
                  <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
                    <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Owed to Members (unspent, current){period === 'today' && ' *'}</p>
                    <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:22, color:'#f0a020', letterSpacing:1 }}>${memberOwed.toFixed(2)}</p>
                    <p style={{ fontSize:10, color:'#3a5a8a', marginTop:4 }}>{totalCirculation.toFixed(0)} coins in wallets × ${(rate||0)}/coin</p>
                  </div>
                  <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
                    <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Owed to Drivers/Staff (earned, uncashed, current){period === 'today' && ' *'}</p>
                    <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:22, color:'#f0a020', letterSpacing:1 }}>${(owedToTeam*(rate||0)).toFixed(2)}</p>
                    <p style={{ fontSize:10, color:'#3a5a8a', marginTop:4 }}>{owedToTeam.toFixed(0)} coins owed × ${(rate||0)}/coin</p>
                  </div>
                </div>
                <div style={{ background:'#0a2010', border:'1px solid #0a3020', borderRadius:14, padding:16, marginBottom:8 }}>
                  <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Profit{period === 'today' && ' *'}</p>
                  <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:30, color:profit >= 0 ? '#1a9e5a' : '#c0101a', letterSpacing:1 }}>${profit.toFixed(2)}</p>
                </div>
                {period === 'today' && (
                  <p style={{ fontSize:10, color:'#3a5a8a', marginBottom:8 }}>* Marked tiles always reflect current standing balances, not just today's change — so Profit here is today's Net Cash Flow minus everything currently owed, not a pure "today's profit."</p>
                )}
              </>
            );
          })()}
          <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:20, lineHeight:1.5 }}>
            Example: a $30-coin delivery that pays the driver 5 coins shows up here immediately as $25 of margin — the driver's 5 coins land in <strong>Owed to Drivers/Staff</strong> the moment the delivery completes, and <strong>Profit</strong> already has it subtracted. You don't need to run cashouts on a schedule to see accurate numbers; cashing a driver/staffer out just moves their coins from "owed" into "Costs (paid out)" on the tiles above — Profit itself doesn't move. Note: Profit only reflects activity since this ledger started, so pre-existing wallet/driver/staff balances count as owed from day one, which can make it look low (even negative) at first with nothing actually wrong.
          </p>

          <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
            <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>By Type</p>
            {Object.entries(ledgerSummary.byType).map(([type, amt]) => (
              <div key={type} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #0a1628', fontSize:13 }}>
                <span style={{ color:'#e8e8e8' }}>{LEDGER_TYPE_LABELS[type] || type}</span>
                <span style={{ color: amt >= 0 ? '#1a9e5a' : '#c0101a', fontFamily:'Bebas Neue,sans-serif' }}>{amt >= 0 ? '+' : ''}${amt.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Coin Economy Stats */}
      <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:12 }}>Coin Economy</p>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          { label:'Coins in Wallets',  val:totalCirculation.toFixed(0), color:'#1e6bff' },
          { label:'Coins Spent',       val:totalSpent.toFixed(0),        color:'#1a9e5a' },
          { label:'Total Orders',      val:totalOrders,                  color:'#e8e8e8' },
          { label:'Avg Order (coins)', val:isNaN(avgOrder)?0:avgOrder.toFixed(0), color:'#e8e8e8' },
        ].map(m=>(
          <div key={m.label} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16 }}>
            <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{m.label}</p>
            <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, color:m.color, letterSpacing:1 }}>{m.val}</p>
          </div>
        ))}
      </div>

      {/* Member Wallet Overview */}
      <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:12 }}>Member Wallets</p>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, marginBottom:24 }}>
        <thead><tr>
          {['M.I.D.','Name','Balance (coins)','% of Total','Adjust'].map(h=>(
            <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'8px 12px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {members.sort((a,b)=>parseFloat(b.wallet||0)-parseFloat(a.wallet||0)).map(m=>{
            const bal = parseFloat(m.wallet||0);
            const pct = totalCirculation > 0 ? ((bal/totalCirculation)*100).toFixed(1) : 0;
            return (
              <tr key={m.mid} onMouseEnter={e=>e.currentTarget.style.background='#06091a'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{ padding:'10px 12px' }}>
                  <span style={{ background:'#0a1530', color:'#1e6bff', borderRadius:6, padding:'2px 8px', fontSize:11, fontFamily:'Bebas Neue,sans-serif' }}>{m.mid}</span>
                </td>
                <td style={{ padding:'10px 12px' }}>
                  <button onClick={()=>setMemberProfile(m)} style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', color:'#e8e8e8', textDecoration:'underline', textDecorationColor:'#1e3a6a' }}>{m.name}</button>
                </td>
                <td style={{ padding:'10px 12px', fontFamily:'Bebas Neue,sans-serif', fontSize:15, color:'#1a9e5a' }}>{bal.toFixed(0)}</td>
                <td style={{ padding:'10px 12px', color:'#3a5a8a' }}>{pct}%</td>
                <td style={{ padding:'10px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <input type="number" placeholder="±coins" title="Coins to add" style={{ width:70, padding:'4px 8px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:7, color:'#e8e8e8', fontSize:12 }}
                      id={`adj-${m.mid}`} />
                    <input type="number" placeholder="$ (opt.)" title="Actual dollars collected — leave blank to estimate from the daily rate" style={{ width:65, padding:'4px 8px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:7, color:'#e8e8e8', fontSize:12 }}
                      id={`adj-dollar-${m.mid}`} />
                    <button onClick={()=>{ const v=document.getElementById(`adj-${m.mid}`).value; const d=document.getElementById(`adj-dollar-${m.mid}`).value; if(v) onTopUp(m.mid,v,d); }}
                      style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                      Apply
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Export */}
      <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, marginBottom:12 }}>Export Data</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {[
          { label:'Order History',     desc:'All orders with totals',     type:'orders'  },
          { label:'Member Wallets',    desc:'Balances and history',        type:'members' },
          { label:'Driver Performance',desc:'Deliveries per driver',       type:'drivers' },
          { label:'Ledger',            desc:'Every revenue & cost entry',  type:'ledger'  },
        ].map(r=>(
          <div key={r.type} style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:18 }}>
            <p style={{ fontSize:14, color:'#e8e8e8', marginBottom:6 }}>{r.label}</p>
            <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:14 }}>{r.desc}</p>
            <button className="btn-blue" onClick={()=>exportData(r.type)}>Export JSON</button>
          </div>
        ))}
      </div>

      {memberProfile && (
        <MemberProfileModal
          member={memberProfile}
          orders={orders}
          members={members}
          staff={staff}
          onSelectMember={setMemberProfile}
          onSelectStaff={(username) => setStaffProfile(staff.find(s => s.username === username))}
          onClose={() => setMemberProfile(null)}
        />
      )}
      {staffProfile && <StaffProfileModal staffMember={staffProfile} members={members} orders={orders} drivers={drivers} staff={staff} onClose={()=>setStaffProfile(null)} />}
    </div>
  );
}

// ================================
// SETTINGS TAB
// Everything here is configuration, not reporting — split out of Reports
// since that tab had turned into a dumping ground for every admin toggle
// that needed a home.
// ================================
function SettingsTab() {
  const [rate, setRate]           = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [rateSaving, setRateSaving] = useState(false);
  const [pointsRate, setPointsRate]           = useState(null);
  const [pointsRateInput, setPointsRateInput] = useState('');
  const [pointsRateSaving, setPointsRateSaving] = useState(false);
  const [feeRate, setFeeRate]           = useState(null);
  const [feeRateInput, setFeeRateInput] = useState('');
  const [feeRateSaving, setFeeRateSaving] = useState(false);
  const [geofenceOn, setGeofenceOn] = useState(null);
  const [geofenceSaving, setGeofenceSaving] = useState(false);
  const [supportPhone, setSupportPhone]           = useState(null);
  const [supportPhoneInput, setSupportPhoneInput] = useState('');
  const [supportPhoneSaving, setSupportPhoneSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/coins/rate').then(r => { setRate(r.data.rate); setRateInput(String(r.data.rate)); }).catch(()=>{});
    axios.get('/api/coins/points-rate').then(r => { setPointsRate(r.data.rate); setPointsRateInput(String(r.data.rate)); }).catch(()=>{});
    axios.get('/api/coins/fee-rate').then(r => { setFeeRate(r.data.rate); setFeeRateInput(String(r.data.rate)); }).catch(()=>{});
    axios.get('/api/settings/geofence').then(r => setGeofenceOn(r.data.enabled)).catch(()=>{});
    axios.get('/api/settings/support-phone').then(r => { setSupportPhone(r.data.phone); setSupportPhoneInput(r.data.phone); }).catch(()=>{});
  }, []);

  async function saveRate() {
    setRateSaving(true);
    try {
      const r = await axios.post('/api/coins/rate', { rate: parseFloat(rateInput) });
      setRate(r.data.rate);
      alert(`✅ Coin rate updated to $${r.data.rate}/coin`);
    } catch(e) { alert(e.response?.data?.error || 'Failed to update rate'); }
    finally { setRateSaving(false); }
  }

  async function savePointsRate() {
    setPointsRateSaving(true);
    try {
      const r = await axios.post('/api/coins/points-rate', { rate: parseFloat(pointsRateInput) });
      setPointsRate(r.data.rate);
      alert(`✅ Points rate updated to $${r.data.rate} spent = 1 point`);
    } catch(e) { alert(e.response?.data?.error || 'Failed to update rate'); }
    finally { setPointsRateSaving(false); }
  }

  async function saveFeeRate() {
    setFeeRateSaving(true);
    try {
      const r = await axios.post('/api/coins/fee-rate', { rate: parseFloat(feeRateInput) });
      setFeeRate(r.data.rate);
      alert(`✅ Cash App fee updated to ${r.data.rate}%`);
    } catch(e) { alert(e.response?.data?.error || 'Failed to update fee'); }
    finally { setFeeRateSaving(false); }
  }

  async function toggleGeofence() {
    setGeofenceSaving(true);
    try {
      const r = await axios.put('/api/settings/geofence', { enabled: !geofenceOn });
      setGeofenceOn(r.data.enabled);
    } catch(e) { alert('Failed to update setting'); }
    finally { setGeofenceSaving(false); }
  }

  async function saveSupportPhone() {
    setSupportPhoneSaving(true);
    try {
      const r = await axios.put('/api/settings/support-phone', { phone: supportPhoneInput });
      setSupportPhone(r.data.phone);
      alert('✅ Support phone updated');
    } catch(e) { alert(e.response?.data?.error || 'Failed to update support phone'); }
    finally { setSupportPhoneSaving(false); }
  }

  return (
    <div>
      <h2 style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:26, letterSpacing:2, marginBottom:4 }}>Settings</h2>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:20 }}>App-wide configuration</p>

      <div style={{ display:'flex', alignItems:'flex-end', gap:10, background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:16 }}>
        <div>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Coin Exchange Rate ($/coin)</p>
          <input type="number" step="0.01" min="0" value={rateInput} onChange={e=>setRateInput(e.target.value)}
            style={{ width:120, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
        </div>
        <button onClick={saveRate} disabled={rateSaving || !rateInput || parseFloat(rateInput) === rate}
          style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
          {rateSaving ? 'Saving...' : 'Update Rate'}
        </button>
        {rate !== null && <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>Current: ${rate}/coin</p>}
      </div>

      <div style={{ display:'flex', alignItems:'flex-end', gap:10, background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:16 }}>
        <div>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Points Rate ($ spent per point)</p>
          <input type="number" step="1" min="1" value={pointsRateInput} onChange={e=>setPointsRateInput(e.target.value)}
            style={{ width:120, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
        </div>
        <button onClick={savePointsRate} disabled={pointsRateSaving || !pointsRateInput || parseFloat(pointsRateInput) === pointsRate}
          style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
          {pointsRateSaving ? 'Saving...' : 'Update Rate'}
        </button>
        {pointsRate !== null && <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>Current: ${pointsRate} spent = 1 point</p>}
      </div>

      <div style={{ display:'flex', alignItems:'flex-end', gap:10, background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:16 }}>
        <div>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Cash App Fee (%)</p>
          <input type="number" step="1" min="0" value={feeRateInput} onChange={e=>setFeeRateInput(e.target.value)}
            style={{ width:120, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
        </div>
        <button onClick={saveFeeRate} disabled={feeRateSaving || feeRateInput === '' || parseFloat(feeRateInput) === feeRate}
          style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
          {feeRateSaving ? 'Saving...' : 'Update Fee'}
        </button>
        {feeRate !== null && <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:10 }}>Current: {feeRate}% on top of the coin exchange rate, charged on every Cash App purchase</p>}
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
        <div>
          <p style={{ fontSize:13, color:'#e8e8e8', marginBottom:2 }}>Require driver to be at the delivery address</p>
          <p style={{ fontSize:11, color:'#3a5a8a' }}>Blocks "Mark Delivered" (and QR scan) unless the driver's location is close to the order's address</p>
        </div>
        <button onClick={toggleGeofence} disabled={geofenceOn === null || geofenceSaving}
          style={{
            background: geofenceOn ? '#0a2010' : '#1a0505', color: geofenceOn ? '#1a9e5a' : '#c0101a',
            border: `1px solid ${geofenceOn ? '#0a3020' : '#3a0a0a'}`, borderRadius:20, padding:'8px 18px',
            fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500, minWidth:70
          }}>
          {geofenceOn === null ? '...' : geofenceOn ? 'ON' : 'OFF'}
        </button>
      </div>

      <div style={{ display:'flex', alignItems:'flex-end', gap:10, background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
        <div>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Support Phone (call-only, shown on member cards)</p>
          <input type="tel" value={supportPhoneInput} onChange={e=>setSupportPhoneInput(e.target.value)} placeholder="+1 (555) 000-0000"
            style={{ width:200, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
        </div>
        <button onClick={saveSupportPhone} disabled={supportPhoneSaving || supportPhoneInput === supportPhone}
          style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
          {supportPhoneSaving ? 'Saving...' : 'Update Phone'}
        </button>
      </div>

      <CashtagsSettings />
      <PaymentTagsSettings method="venmo" title="Venmo Handles" fieldLabel="Venmo handle" placeholder="handle (no @)" prefix="@"
        description="Customers are handed one of these when they request a Venmo payment link. You still confirm every payment by hand before crediting coins — this only decides which handle they're told to pay." />
      <PaymentTagsSettings method="chime" title="Chime $Signs" fieldLabel="Chime $Sign" placeholder="ChimeSign (no $)" prefix="$"
        description="Customers are shown one of these when they choose to pay with Chime. No app link — Chime's Pay Friends only works inside their own app, so this is shown as plain text to send to." />
      <PaymentTagsSettings method="applepay" title="Apple Pay Phone Numbers" fieldLabel="Phone number" placeholder="(555) 000-0000" inputType="tel"
        description="Customers are shown one of these when they choose to pay with Apple Pay (person-to-person, via Messages). No app link — this is shown as plain text to text the payment to." />
      <ReferralSettings />

      <div style={{ marginTop:32 }}>
        <BackupSection />
      </div>
    </div>
  );
}

// ================================
// CASH APP CASHTAG POOL
// Multiple approved cashtags on file instead of one — buy-link picks
// one (random in auto mode, the pinned one in manual mode) and the admin
// still verifies every payment by hand either way; this just decides
// which link a customer gets handed.
// ================================
function CashtagsSettings() {
  const [config, setConfig]     = useState(null); // { mode, selectedId, tags }
  const [loading, setLoading]   = useState(true);
  const [newTag, setNewTag]     = useState({ cashtag:'', label:'' });
  const [adding, setAdding]     = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [busyId, setBusyId]     = useState(null); // id currently being toggled/selected/removed

  useEffect(() => {
    axios.get('/api/settings/cashtags').then(r => setConfig(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  async function addTag() {
    if (!newTag.cashtag.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post('/api/settings/cashtags', newTag);
      setConfig(r.data);
      setNewTag({ cashtag:'', label:'' });
    } catch(e) { alert(e.response?.data?.error || 'Failed to add cashtag'); }
    finally { setAdding(false); }
  }

  async function toggleActive(t) {
    setBusyId(t.id);
    try {
      const r = await axios.put(`/api/settings/cashtags/${t.id}`, { active: !t.active });
      setConfig(r.data);
    } catch(e) { alert('Failed to update cashtag'); }
    finally { setBusyId(null); }
  }

  async function removeTag(t) {
    if (!window.confirm(`Remove $${t.cashtag} from the pool?`)) return;
    setBusyId(t.id);
    try {
      const r = await axios.delete(`/api/settings/cashtags/${t.id}`);
      setConfig(r.data);
    } catch(e) { alert('Failed to remove cashtag'); }
    finally { setBusyId(null); }
  }

  async function selectTag(id) {
    setBusyId(id);
    try {
      const r = await axios.put('/api/settings/cashtags/selected', { id });
      setConfig(r.data);
    } catch(e) { alert('Failed to pin cashtag'); }
    finally { setBusyId(null); }
  }

  async function setMode(mode) {
    if (!config || config.mode === mode) return;
    setModeSaving(true);
    try {
      const r = await axios.put('/api/settings/cashtags/mode', { mode });
      setConfig(r.data);
    } catch(e) { alert('Failed to update mode'); }
    finally { setModeSaving(false); }
  }

  return (
    <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
      <p style={{ fontSize:13, color:'#e8e8e8', marginBottom:2 }}>Cash App Cashtags</p>
      <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:14 }}>
        Customers are handed one of these when they request a payment link. You still confirm every payment by hand before crediting coins — this only decides which $cashtag they're told to pay.
      </p>

      {loading || !config ? (
        <p style={{ fontSize:12, color:'#3a5a8a' }}>Loading...</p>
      ) : (
        <>
          {/* Auto / Manual mode toggle */}
          <div style={{ display:'flex', gap:8, marginBottom:14 }}>
            {['auto', 'manual'].map(m => (
              <button key={m} onClick={()=>setMode(m)} disabled={modeSaving}
                style={{
                  background: config.mode === m ? '#0a1530' : 'transparent',
                  color: config.mode === m ? '#1e6bff' : '#3a5a8a',
                  border: `1px solid ${config.mode === m ? '#1e3a6a' : '#0a1628'}`,
                  borderRadius:8, padding:'7px 16px', fontSize:12, cursor:'pointer',
                  fontFamily:'DM Sans,sans-serif', fontWeight: config.mode === m ? 500 : 400, textTransform:'capitalize'
                }}>
                {m === 'auto' ? 'Auto (random)' : 'Manual (pick one)'}
              </button>
            ))}
          </div>

          {config.tags.length === 0 ? (
            <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:14 }}>No cashtags on file yet — add one below.</p>
          ) : (
            <div style={{ marginBottom:14 }}>
              {config.tags.map(t => {
                const isPinned = config.mode === 'manual' && config.selectedId === t.id;
                return (
                  <div key={t.id} style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'10px 12px', marginBottom:6, borderRadius:10,
                    background: isPinned ? '#0a1530' : '#0a0f20',
                    border: `1px solid ${isPinned ? '#1e3a6a' : '#0d1e3a'}`, opacity: t.active ? 1 : 0.5
                  }}>
                    <div>
                      <p style={{ fontSize:14, color:'#e8e8e8' }}>
                        ${t.cashtag} {t.label && <span style={{ fontSize:11, color:'#3a5a8a' }}>— {t.label}</span>}
                      </p>
                      {!t.active && <p style={{ fontSize:10, color:'#c0101a', marginTop:2 }}>Inactive — never picked</p>}
                      {isPinned && <p style={{ fontSize:10, color:'#1e6bff', marginTop:2 }}>Pinned for manual mode</p>}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      {config.mode === 'manual' && t.active && !isPinned && (
                        <button onClick={()=>selectTag(t.id)} disabled={busyId === t.id}
                          style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                          Use this one
                        </button>
                      )}
                      <button onClick={()=>toggleActive(t)} disabled={busyId === t.id}
                        style={{ background: t.active ? '#0a2010' : '#1a0505', color: t.active ? '#1a9e5a' : '#c0101a', border:`1px solid ${t.active ? '#0a3020' : '#3a0a0a'}`, borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        {t.active ? 'Active' : 'Inactive'}
                      </button>
                      <button onClick={()=>removeTag(t)} disabled={busyId === t.id}
                        style={{ background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add new cashtag */}
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input placeholder="cashtag (no $)" value={newTag.cashtag} onChange={e=>setNewTag(p=>({...p, cashtag:e.target.value}))}
              style={{ flex:1, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            <input placeholder="label (optional)" value={newTag.label} onChange={e=>setNewTag(p=>({...p, label:e.target.value}))}
              style={{ flex:1, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            <button onClick={addTag} disabled={adding || !newTag.cashtag.trim()}
              style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ================================
// VENMO / CHIME / APPLE PAY TAG POOLS
// Same exact pattern as CashtagsSettings above, generalized — one of these
// renders per method with its own label/placeholder, talking to the
// generalized /api/settings/payment-tags/:method routes (Cash App keeps
// its own separate, untouched /api/settings/cashtags routes/component).
// ================================
function PaymentTagsSettings({ method, title, description, fieldLabel, placeholder, prefix = '', inputType = 'text' }) {
  const [config, setConfig]     = useState(null); // { mode, selectedId, tags }
  const [loading, setLoading]   = useState(true);
  const [newTag, setNewTag]     = useState({ value:'', label:'' });
  const [adding, setAdding]     = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [busyId, setBusyId]     = useState(null);

  useEffect(() => {
    axios.get(`/api/settings/payment-tags/${method}`).then(r => setConfig(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [method]);

  async function addTag() {
    if (!newTag.value.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`/api/settings/payment-tags/${method}`, newTag);
      setConfig(r.data);
      setNewTag({ value:'', label:'' });
    } catch(e) { alert(e.response?.data?.error || `Failed to add ${fieldLabel.toLowerCase()}`); }
    finally { setAdding(false); }
  }

  async function toggleActive(t) {
    setBusyId(t.id);
    try {
      const r = await axios.put(`/api/settings/payment-tags/${method}/${t.id}`, { active: !t.active });
      setConfig(r.data);
    } catch(e) { alert(`Failed to update ${fieldLabel.toLowerCase()}`); }
    finally { setBusyId(null); }
  }

  async function removeTag(t) {
    if (!window.confirm(`Remove ${prefix}${t.value} from the pool?`)) return;
    setBusyId(t.id);
    try {
      const r = await axios.delete(`/api/settings/payment-tags/${method}/${t.id}`);
      setConfig(r.data);
    } catch(e) { alert(`Failed to remove ${fieldLabel.toLowerCase()}`); }
    finally { setBusyId(null); }
  }

  async function selectTag(id) {
    setBusyId(id);
    try {
      const r = await axios.put(`/api/settings/payment-tags/${method}/selected`, { id });
      setConfig(r.data);
    } catch(e) { alert(`Failed to pin ${fieldLabel.toLowerCase()}`); }
    finally { setBusyId(null); }
  }

  async function setMode(mode) {
    if (!config || config.mode === mode) return;
    setModeSaving(true);
    try {
      const r = await axios.put(`/api/settings/payment-tags/${method}/mode`, { mode });
      setConfig(r.data);
    } catch(e) { alert('Failed to update mode'); }
    finally { setModeSaving(false); }
  }

  return (
    <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
      <p style={{ fontSize:13, color:'#e8e8e8', marginBottom:2 }}>{title}</p>
      <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:14 }}>{description}</p>

      {loading || !config ? (
        <p style={{ fontSize:12, color:'#3a5a8a' }}>Loading...</p>
      ) : (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:14 }}>
            {['auto', 'manual'].map(m => (
              <button key={m} onClick={()=>setMode(m)} disabled={modeSaving}
                style={{
                  background: config.mode === m ? '#0a1530' : 'transparent',
                  color: config.mode === m ? '#1e6bff' : '#3a5a8a',
                  border: `1px solid ${config.mode === m ? '#1e3a6a' : '#0a1628'}`,
                  borderRadius:8, padding:'7px 16px', fontSize:12, cursor:'pointer',
                  fontFamily:'DM Sans,sans-serif', fontWeight: config.mode === m ? 500 : 400, textTransform:'capitalize'
                }}>
                {m === 'auto' ? 'Auto (random)' : 'Manual (pick one)'}
              </button>
            ))}
          </div>

          {config.tags.length === 0 ? (
            <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:14 }}>None on file yet — add one below.</p>
          ) : (
            <div style={{ marginBottom:14 }}>
              {config.tags.map(t => {
                const isPinned = config.mode === 'manual' && config.selectedId === t.id;
                return (
                  <div key={t.id} style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'10px 12px', marginBottom:6, borderRadius:10,
                    background: isPinned ? '#0a1530' : '#0a0f20',
                    border: `1px solid ${isPinned ? '#1e3a6a' : '#0d1e3a'}`, opacity: t.active ? 1 : 0.5
                  }}>
                    <div>
                      <p style={{ fontSize:14, color:'#e8e8e8' }}>
                        {prefix}{t.value} {t.label && <span style={{ fontSize:11, color:'#3a5a8a' }}>— {t.label}</span>}
                      </p>
                      {!t.active && <p style={{ fontSize:10, color:'#c0101a', marginTop:2 }}>Inactive — never picked</p>}
                      {isPinned && <p style={{ fontSize:10, color:'#1e6bff', marginTop:2 }}>Pinned for manual mode</p>}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      {config.mode === 'manual' && t.active && !isPinned && (
                        <button onClick={()=>selectTag(t.id)} disabled={busyId === t.id}
                          style={{ background:'#0a1530', color:'#1e6bff', border:'1px solid #1e3a6a', borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                          Use this one
                        </button>
                      )}
                      <button onClick={()=>toggleActive(t)} disabled={busyId === t.id}
                        style={{ background: t.active ? '#0a2010' : '#1a0505', color: t.active ? '#1a9e5a' : '#c0101a', border:`1px solid ${t.active ? '#0a3020' : '#3a0a0a'}`, borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        {t.active ? 'Active' : 'Inactive'}
                      </button>
                      <button onClick={()=>removeTag(t)} disabled={busyId === t.id}
                        style={{ background:'#1a0505', color:'#c0101a', border:'1px solid #3a0a0a', borderRadius:7, padding:'5px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type={inputType} placeholder={placeholder} value={newTag.value} onChange={e=>setNewTag(p=>({...p, value:e.target.value}))}
              style={{ flex:1, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            <input placeholder="label (optional)" value={newTag.label} onChange={e=>setNewTag(p=>({...p, label:e.target.value}))}
              style={{ flex:1, padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:13, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            <button onClick={addTag} disabled={adding || !newTag.value.trim()}
              style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ================================
// REFERRAL REWARDS
// Was a hardcoded "100 points + 20 coins" string on the customer Rewards
// page with no admin control at all — now a real setting, admin-editable
// here, and the one source of truth the customer page actually reads.
// ================================
function ReferralSettings() {
  const [settings, setSettings] = useState(null); // { enabled, referrerPoints, referrerCoins, friendCoins, promoActive, effectiveReferrerPoints, effectiveReferrerCoins }
  const [form, setForm]         = useState({ referrerPoints:'', referrerCoins:'', friendCoins:'' });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);

  useEffect(() => {
    axios.get('/api/settings/referral').then(r => {
      setSettings(r.data);
      setForm({ referrerPoints: String(r.data.referrerPoints), referrerCoins: String(r.data.referrerCoins), friendCoins: String(r.data.friendCoins) });
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const r = await axios.put('/api/settings/referral', {
        enabled: settings.enabled,
        referrerPoints: parseFloat(form.referrerPoints),
        referrerCoins: parseFloat(form.referrerCoins),
        friendCoins: parseFloat(form.friendCoins)
      });
      setSettings(p => ({ ...p, ...r.data, effectiveReferrerPoints: p.promoActive ? p.effectiveReferrerPoints : r.data.referrerPoints, effectiveReferrerCoins: p.promoActive ? p.effectiveReferrerCoins : r.data.referrerCoins }));
      alert('✅ Referral settings saved');
    } catch(e) { alert(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function toggleEnabled() {
    setTogglingEnabled(true);
    try {
      const r = await axios.put('/api/settings/referral', {
        enabled: !settings.enabled,
        referrerPoints: settings.referrerPoints, referrerCoins: settings.referrerCoins, friendCoins: settings.friendCoins
      });
      setSettings(p => ({ ...p, enabled: r.data.enabled }));
    } catch(e) { alert('Failed to update setting'); }
    finally { setTogglingEnabled(false); }
  }

  const dirty = settings && (
    form.referrerPoints !== String(settings.referrerPoints) ||
    form.referrerCoins !== String(settings.referrerCoins) ||
    form.friendCoins !== String(settings.friendCoins)
  );

  return (
    <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:16, marginBottom:24 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:2 }}>
        <p style={{ fontSize:13, color:'#e8e8e8' }}>Referral Rewards</p>
        {settings && (
          <button onClick={toggleEnabled} disabled={loading || togglingEnabled}
            style={{
              background: settings.enabled ? '#0a2010' : '#1a0505', color: settings.enabled ? '#1a9e5a' : '#c0101a',
              border: `1px solid ${settings.enabled ? '#0a3020' : '#3a0a0a'}`, borderRadius:20, padding:'6px 16px',
              fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500, minWidth:70
            }}>
            {togglingEnabled ? '...' : settings.enabled ? 'ON' : 'OFF'}
          </button>
        )}
      </div>
      <p style={{ fontSize:11, color:'#3a5a8a', marginBottom:14 }}>
        What a member earns for referring a friend — shown on their Rewards page as "Earn {settings ? settings.referrerPoints : '…'} points + {settings ? settings.referrerCoins : '…'} coins per friend." Off means referral links still track signups, they just don't pay out.
      </p>

      {loading || !settings ? (
        <p style={{ fontSize:12, color:'#3a5a8a' }}>Loading...</p>
      ) : (
        <>
          {settings.promoActive && (
            <p style={{ fontSize:11, color:'#f0a020', marginBottom:12 }}>
              ⚡ A live promo is currently boosting this to {settings.effectiveReferrerPoints} points + {settings.effectiveReferrerCoins} coins — the amounts below are the standing default underneath it.
            </p>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:12 }}>
            <div>
              <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Referrer Points</p>
              <input type="number" min="0" value={form.referrerPoints} onChange={e=>setForm(p=>({...p, referrerPoints:e.target.value}))}
                style={{ width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            </div>
            <div>
              <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Referrer Coins</p>
              <input type="number" min="0" value={form.referrerCoins} onChange={e=>setForm(p=>({...p, referrerCoins:e.target.value}))}
                style={{ width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            </div>
            <div>
              <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>New Member's Own Bonus (coins)</p>
              <input type="number" min="0" value={form.friendCoins} onChange={e=>setForm(p=>({...p, friendCoins:e.target.value}))}
                style={{ width:'100%', padding:'8px 10px', background:'#0a0f20', border:'1px solid #0d1e3a', borderRadius:8, color:'#e8e8e8', fontSize:14, outline:'none', fontFamily:'DM Sans,sans-serif' }} />
            </div>
          </div>
          <button onClick={save} disabled={saving || !dirty}
            style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}
    </div>
  );
}

// ================================
// DAILY BACKUP + VERIFICATION
// ================================
function BackupSection() {
  const [latest, setLatest] = useState(null);
  const [backups, setBackups] = useState([]);
  const [running, setRunning] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => { load(); }, []);

  function load() {
    axios.get('/api/backup/latest').then(r => setLatest(r.data.latest)).catch(()=>{});
    axios.get('/api/backup/list').then(r => setBackups(r.data.backups || [])).catch(()=>{});
  }

  async function runNow() {
    setRunning(true);
    try {
      await axios.post('/api/backup/run');
      load();
    } catch(e) { alert('Backup failed'); }
    finally { setRunning(false); }
  }

  async function downloadBackup(filename) {
    try {
      const res = await axios.get(`/api/backup/${filename}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Failed to download backup'); }
  }

  const v = latest?.verification;
  const visibleBackups = showAll ? backups : backups.slice(0, 10);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1 }}>Daily Backup</p>
        <button onClick={runNow} disabled={running} style={{ background:'#0a2010', color:'#1a9e5a', border:'1px solid #0a3020', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontWeight:500 }}>
          {running ? 'Running...' : 'Run Backup Now'}
        </button>
      </div>
      <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:16 }}>
        Runs automatically every night at 3am. Each one is checked against the one before it so you'd know if anything went missing.
        Backups are kept for 30 days by default, then deleted automatically — the list below won't grow forever. Each one can also be
        copied to Google Drive as a second copy; set GOOGLE_DRIVE_BACKUP_FOLDER_ID (see .env.example for the one-time setup) to turn that on.
      </p>

      {latest ? (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:18, marginBottom:16 }}>
          <p style={{ fontSize:11, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
            Last backup — {new Date(latest.timestamp).toLocaleString()}
            {' · '}
            <span style={{ color: latest.driveUploaded ? '#1a9e5a' : '#3a5a8a' }}>
              {latest.driveUploaded ? '✓ Also saved to Google Drive' : 'Local copy only'}
            </span>
          </p>
          {v?.isFirstBackup ? (
            <p style={{ fontSize:13, color:'#e8e8e8' }}>First backup on record — nothing to compare it against yet.</p>
          ) : v ? (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
                <div>
                  <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1 }}>Members</p>
                  <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, color:'#e8e8e8' }}>{v.memberCount.after}</p>
                </div>
                <div>
                  <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1 }}>Orders</p>
                  <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, color:'#e8e8e8' }}>{v.orderCount.after}</p>
                </div>
                <div>
                  <p style={{ fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1 }}>Coins in Wallets</p>
                  <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:20, color: v.totalWalletCoins.change < 0 ? '#c0101a' : '#1a9e5a' }}>
                    {v.totalWalletCoins.after} {v.totalWalletCoins.change !== 0 && `(${v.totalWalletCoins.change > 0 ? '+' : ''}${v.totalWalletCoins.change})`}
                  </p>
                </div>
              </div>
              {v.notes.map((n, i) => (
                <p key={i} style={{ fontSize:12, color: n.startsWith('⚠') ? '#f0a020' : '#1a9e5a', marginBottom:4 }}>{n}</p>
              ))}
            </>
          ) : null}
        </div>
      ) : (
        <div style={{ background:'#06091a', border:'1px solid #0a1628', borderRadius:14, padding:18, marginBottom:16, textAlign:'center' }}>
          <p style={{ fontSize:13, color:'#3a5a8a' }}>No backups yet — click "Run Backup Now" to create the first one.</p>
        </div>
      )}

      {backups.length > 0 && (
        <>
          <div className="table-wrap" style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr>
                {['Date','Members','Orders','Drivers','Drive',''].map(h=>(
                  <th key={h} style={{ textAlign:'left', fontSize:10, color:'#3a5a8a', textTransform:'uppercase', letterSpacing:1, padding:'8px 12px', borderBottom:'1px solid #0a1628', fontWeight:400 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {visibleBackups.map(b => (
                  <tr key={b.filename}>
                    <td style={{ padding:'8px 12px', color:'#e8e8e8' }}>{new Date(b.timestamp).toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', color:'#e8e8e8' }}>{b.memberCount}</td>
                    <td style={{ padding:'8px 12px', color:'#e8e8e8' }}>{b.orderCount}</td>
                    <td style={{ padding:'8px 12px', color:'#e8e8e8' }}>{b.driverCount}</td>
                    <td style={{ padding:'8px 12px', color: b.driveUploaded ? '#1a9e5a' : '#3a5a8a' }}>{b.driveUploaded ? '✓' : '—'}</td>
                    <td style={{ padding:'8px 12px' }}>
                      <button onClick={()=>downloadBackup(b.filename)} style={{ background:'none', border:'1px solid #0d1e3a', color:'#1e6bff', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {backups.length > 10 && (
            <button onClick={()=>setShowAll(!showAll)} style={{ background:'none', border:'1px solid #0d1e3a', color:'#3a5a8a', borderRadius:9, padding:'7px 14px', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif', marginTop:12 }}>
              {showAll ? 'Show Latest 10' : `Show All (${backups.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ================================
// OFFICE ACCESS — admin-only. Create a login for an office employee and
// pick which sidebar sections they can reach; suspend one on the spot if
// needed. The server independently re-checks each account's live
// permissions on every request (see requireSection in middleware/auth.js)
// — this panel is just where an admin manages that set, not the
// enforcement itself.
// ================================
function OfficeAccessPanel({ onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchAccounts(); }, []);

  async function fetchAccounts() {
    setLoading(true);
    try {
      const r = await axios.get('/api/office');
      setAccounts(r.data.accounts || []);
    } catch(e) {}
    finally { setLoading(false); }
  }

  async function toggleStatus(username, currentStatus) {
    const next = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`/api/office/${encodeURIComponent(username)}/status`, { status: next });
      fetchAccounts();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update status'); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:520, maxHeight:'85vh', overflowY:'auto', background:'#06091a', border:'1px solid #1e3a6a', borderRadius:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #0a1628' }}>
          <p style={{ fontFamily:'Bebas Neue,sans-serif', fontSize:18, letterSpacing:1, color:'#1e6bff' }}>Office Access</p>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#3a5a8a', fontSize:20, cursor:'pointer' }}>×</button>
        </div>

        <div style={{ padding:'16px 20px' }}>
          <p style={{ fontSize:12, color:'#3a5a8a', marginBottom:16, lineHeight:1.5 }}>
            Give an office employee their own login into just the sections they need — never the whole admin panel.
          </p>

          {loading ? (
            <p style={{ color:'#3a5a8a', fontSize:13 }}>Loading...</p>
          ) : accounts.length === 0 ? (
            <p style={{ color:'#3a5a8a', fontSize:13, marginBottom:16 }}>No office accounts yet.</p>
          ) : (
            <div style={{ marginBottom:16 }}>
              {accounts.map(a => (
                <div key={a.username} style={{ padding:'10px 0', borderBottom:'1px solid #0a1628' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div>
                      <p style={{ fontSize:13, color:'#e8e8e8' }}>{a.name} <span style={{ color:'#3a5a8a' }}>— {a.username}</span></p>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:6 }}>
                        {a.permissions.map(p => (
                          <span key={p} style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:'#0a1530', color:'#1e6bff' }}>{p}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => toggleStatus(a.username, a.status)} style={{
                      flexShrink:0, background:'none', border:`1px solid ${a.status==='active' ? '#3a0a0a' : '#0a3020'}`,
                      color: a.status==='active' ? '#c0101a' : '#1a9e5a', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'DM Sans,sans-serif'
                    }}>
                      {a.status === 'active' ? 'Suspend' : 'Reactivate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAddForm ? (
            <AddOfficeAccountForm onDone={() => { setShowAddForm(false); fetchAccounts(); }} onCancel={() => setShowAddForm(false)} />
          ) : (
            <button className="btn-primary" onClick={() => setShowAddForm(true)}>+ Add Office Employee</button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddOfficeAccountForm({ onDone, onCancel }) {
  const [name, setName]         = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [picked, setPicked]     = useState([]);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  function toggleSection(s) {
    setPicked(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (picked.length === 0) { setError('Pick at least one section'); return; }
    setSaving(true);
    try {
      await axios.post('/api/office', { name, username, password, permissions: picked });
      onDone();
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop:8, borderTop:'1px solid #0a1628', paddingTop:16 }}>
      <div className="field">
        <label>Name</label>
        <input type="text" value={name} onChange={e=>setName(e.target.value)} required />
      </div>
      <div className="field">
        <label>Username</label>
        <input type="text" value={username} onChange={e=>setUsername(e.target.value)} required />
      </div>
      <div className="field">
        <label>Password</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
      </div>
      <div className="field">
        <label>Sections They Can Access</label>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:6 }}>
          {TABS.map(s => (
            <label key={s} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#e8e8e8', cursor:'pointer' }}>
              <input type="checkbox" checked={picked.includes(s)} onChange={()=>toggleSection(s)} />
              {s}
            </label>
          ))}
        </div>
      </div>
      {error && <p style={{ color:'#c0101a', fontSize:12, marginBottom:10 }}>{error}</p>}
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button type="button" onClick={onCancel} style={{ flex:1, background:'none', border:'1px solid #0d1e3a', color:'#3a5a8a', borderRadius:9, padding:'10px', fontSize:13, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Cancel</button>
        <button className="btn-primary" type="submit" disabled={saving} style={{ flex:1 }}>{saving ? 'Creating...' : 'Create Account'}</button>
      </div>
    </form>
  );
}
